/**
 * Vanity Builder — Interactive product configurator
 *
 * Manages step-by-step selection of vanity options:
 *   1. Size & Bowl Position (selects Shopify variant)
 *   2. Cabinet Finish (line item property)
 *   3. Benchtop (line item property)
 *   4. Hardware (line item property)
 *   5. Basin (separate cart item)
 *   6. Taphole (line item property)
 *   7. Plug & Waste (line item property)
 */

(function () {
  'use strict';

  const builder = document.getElementById('vanity-builder');
  if (!builder) return;

  // Renumber visible steps sequentially so skipped steps don't leave gaps
  // in the "Step 0N" labels. Liquid hard-codes step numbers (Step 01..07)
  // but conditional step wrappers remove the steps that don't apply to
  // this vanity. Without this pass, a vanity that skips steps 3/4/5/6
  // would display "Step 01, Step 02, Step 07" — fine functionally, ugly
  // visually. This pass walks the currently-rendered .vb-step elements
  // in DOM order and rewrites each Step NN eyebrow text accordingly.
  var vbSteps = builder.querySelectorAll('.vb-step');
  vbSteps.forEach(function (stepEl, idx) {
    var eyebrow = stepEl.querySelector('.vb-step__eyebrow-step');
    if (!eyebrow) return;
    var n = idx + 1;
    eyebrow.textContent = 'Step ' + (n < 10 ? '0' + n : n);
    stepEl.setAttribute('data-step', n);
  });

  // ── Config from metaobjects (inline JSON from Liquid) ─────────
  var configEl = document.getElementById('vb-config-json');
  var VB_CONFIG = configEl ? JSON.parse(configEl.textContent) : {};

  // ── State ──────────────────────────────────────────────────────
  const state = {
    size: null,
    bowl: null,
    variantId: null,
    variantPrice: 0,
    variantComparePrice: 0,
    finish: null,
    benchtopName: null,
    benchtopMaterial: null,
    hardware: null,
    hardwareType: null,
    handleFinish: null,
    handleProductHandle: null,
    handleCategory: null, // 'standard' | 'premium' | null — drives the Cart Transform $20/handle surcharge

    basinHandle: '',
    basinKey: '',
    basinName: 'No Basin',
    basinFinish: null,
    basinVariantId: null,
    basinPrice: 0,
    pwFinish: '',
    pwName: 'No Plug & Waste',
    pwHandle: '',
    pwVariantId: null,
    taphole: null,
    tapholeLeft: null,
    tapholeRight: null,
    isDouble: false,
    // Cabinet type (used when product has multiple cabinet configurations, e.g. Archie All Door / Door & Drawer)
    cabinetType: null,
    // Top type fields (used when VB_CONFIG.topTypes exists)
    topType: null,
    skipBenchtop: false,
    skipBasin: false,
    // Cabinet Only (Phase E.2) — also flips these so Handle/Taphole steps
    // swap to the Not Required badge instead of blocking ATC.
    skipHardware: false,
    skipTaphole: false,
    // Imperial Top et al. lock the taphole choice and show an explanatory
    // note instead of cards. Set true when a non-empty taphole_note is
    // active for the current integrated top.
    tapholeLocked: false,
    thickness: null,
    thicknessSurcharge: 0,
    // Hinge direction (Left / Right) — only collected when the vanity's
    // config has offers_hinge_selection=true. Emitted as a "Door Hinge"
    // line item property on the vanity cart line.
    hinge: null,
    // Drawer side pick (Left-hand / Right-hand) - only meaningful on sizes
    // listed in the config's drawer_side_option_values. Other sizes derive
    // their value in resolveDrawerSide(); the "Drawer Side" line item
    // property is emitted on every order of a drawer-configured vanity.
    drawer: null,
    // Hinging pick (Standard / Surround) - only meaningful on sizes listed
    // in hinging_option_values (Zurich Shaving Cabinet 1500/1800). Other
    // sizes derive "Standard" in resolveHinging(); the "Hinging" property
    // is emitted on every order of a hinging-configured product.
    hinging: null,
  };

  // ── Step detection helpers ─────────────────────────────────────
  var hasTopTypes = !!(VB_CONFIG.topTypes && VB_CONFIG.topTypes.length);
  var hasTopVariants = !!(VB_CONFIG.topVariants && VB_CONFIG.topVariants.length);
  // Use data-conditional-step (stable, set in Liquid) rather than data-step,
  // because the renumbering pass above rewrites data-step to be sequential
  // for visual "Step 0N" labels — so a vanity that skips e.g. benchtop+hardware
  // would have its P&W step relabelled to data-step="3", causing these flags
  // to misfire.
  var hasBenchtopStep = !!builder.querySelector('[data-conditional-step="benchtop"]');
  var hasHardwareStep = !!builder.querySelector('[data-conditional-step="hardware"]');
  var hasBasinStep = !!builder.querySelector('[data-conditional-step="basin"]');
  var hasHingeStep = !!builder.querySelector('[data-conditional-step="hinge"]');
  var hasFixedTaphole = !!(VB_CONFIG.fixedTapholeOptions && VB_CONFIG.fixedTapholeOptions.length);
  // More than one fixed option = a REQUIRED customer choice (Zurich family
  // "No Taphole / 1 Taphole / 3 Taphole"). A single option (Drift) keeps
  // the step hidden and auto-satisfied, unchanged.
  var hasFixedTapholeChoice = hasFixedTaphole && VB_CONFIG.fixedTapholeOptions.length > 1;
  // Drawer-side feature: option1 values where the customer picks left- or
  // right-hand drawers. Empty/absent = feature off (all pre-Zurich vanities).
  var drawerSideValues = VB_CONFIG.drawerSideOptionValues || [];
  var hasDrawerFeature = drawerSideValues.length > 0;
  // Hinging feature (Zurich Shaving Cabinet): option1 values where the
  // customer picks Standard vs Surround hinging; other sizes derive
  // "Standard". Empty/absent = feature off.
  var hingingValues = VB_CONFIG.hingingOptionValues || [];
  var hasHingingFeature = hingingValues.length > 0;
  // Sizes-only products (Zurich Shaving Cabinet): plain "600mm" option1
  // with no bowl axis - the Liquid stamps data-variant-id on size cards
  // only in that mode, and a size click commits the variant directly.
  var sizesOnly = !!builder.querySelector('.vb-card--size[data-variant-id]');

  /** Resolve variant from size + bowl + top type using the lookup map.
   *  Key format matches Shopify's `option1|option2` — option1 is
   *  `{size}mm {bowl}` for most vanities, or `{size}mm {bowl} ({cabinetType})`
   *  for vanities that expose cabinet types as part of the size option
   *  (e.g. Archie's "1350mm Centre Bowl (All Door)"). */
  function resolveVariant() {
    if (!VB_CONFIG.variantLookup) return;
    if (!state.size || !state.bowl || !state.topType) return;

    var sizeBowl = state.size + 'mm ' + state.bowl;
    if (state.cabinetType) sizeBowl += ' (' + state.cabinetType + ')';
    var key = sizeBowl + '|' + state.topType;
    var v = VB_CONFIG.variantLookup[key];
    if (v) {
      state.variantId = String(v.id);
      state.variantPrice = v.price;
      state.variantComparePrice = v.compareAtPrice || 0;
      state.variantAvailable = v.available !== false;
      updatePrice();
      updateSkuDisplay();
      updateATCState();
    }
  }

  /** Show/hide a conditional step by its data-conditional-step attribute */
  function toggleConditionalStep(stepName, show) {
    var step = $q('[data-conditional-step="' + stepName + '"]');
    if (step) {
      step.classList.toggle('d-none', !show);
    }
  }

  /** Swap a step's eyebrow pill between its normal state
   *  (required/optional/complete) and "Not Required". Also mirrors the
   *  swap on the matching sidebar row so the build summary reads
   *  consistently. `sidebarSummaryKeys` is the list of data-summary keys
   *  that belong to this step (e.g. ['taphole','taphole-double']).
   *  The sidebar row's value span is intentionally left empty — the
   *  "Not Required" pill is the single signal. The thumb image gets
   *  hidden via the `.vb-sidebar__item--not-required` modifier class so
   *  broken-image placeholders don't render. */
  function setStepNotRequired(stepName, sidebarSummaryKeys, isNotRequired) {
    var step = $q('[data-conditional-step="' + stepName + '"]');
    if (step) step.classList.toggle('vb-step--not-required', isNotRequired);

    (sidebarSummaryKeys || []).forEach(function (key) {
      $qa('[data-summary="' + key + '"]').forEach(function (row) {
        row.classList.toggle('vb-sidebar__item--not-required', isNotRequired);
        var tag = row.querySelector('.vb-sidebar__tag');
        if (tag) {
          // Remember the original tag class/text on first flip so we can
          // restore it later. Avoids baking a specific prior state (required
          // vs optional) into this helper — each row knows its own default.
          if (isNotRequired) {
            if (!tag.dataset.vbOriginalText) {
              tag.dataset.vbOriginalText = tag.textContent;
              tag.dataset.vbOriginalClass = tag.className;
            }
            tag.textContent = 'Not Required';
            tag.className = 'vb-sidebar__tag vb-sidebar__tag--not-required';
          } else if (tag.dataset.vbOriginalText) {
            tag.textContent = tag.dataset.vbOriginalText;
            tag.className = tag.dataset.vbOriginalClass;
            delete tag.dataset.vbOriginalText;
            delete tag.dataset.vbOriginalClass;
          }
        }
      });
    });
  }

  /** Apply the full "Cabinet Only" UI state. Called from the bowl-card
   *  handler when the customer selects a Cabinet Only card. Sets skip
   *  flags, swaps downstream step pills to Not Required, and clears any
   *  stale selections in basin/handle/taphole/P&W so a later mode switch
   *  resets from a clean slate. */
  function enterCabinetOnlyMode() {
    // Mirror enterIntegratedTopMode's leading reset: if the customer is
    // switching from Imperial Top / Matte Ceramic to Cabinet Only, the
    // integrated-top side-effects (benchtop finish restriction, taphole
    // note, basin-integrated UI) must be cleared first.
    resetIntegratedTopMode();

    state.topType = 'Cabinet Only';
    state.benchtopName = 'Cabinet Only';
    state.benchtopMaterial = null;
    state.skipBenchtop = true;
    state.skipBasin = true;
    state.skipHardware = true;
    state.skipTaphole = true;
    state.thickness = null;
    state.thicknessSurcharge = 0;

    // Clear downstream selections so returning to a regular bowl later
    // starts from a clean slate.
    $qa('.vb-card--basin').forEach(function (c) { c.classList.remove('vb-card--active'); });
    state.basinKey = '';
    state.basinName = 'No Basin';
    state.basinHandle = '';
    state.basinVariantId = null;
    state.basinPrice = 0;
    state.basinFinish = null;
    state.basinTouched = true;
    $qa('.vb-card--hardware').forEach(function (c) { c.classList.remove('vb-card--active'); });
    state.hardware = null;
    state.hardwareType = null;
    state.handleFinish = null;
    state.handleProductHandle = null;
    state.handleCategory = null;
    state.taphole = null;
    state.tapholeLeft = null;
    state.tapholeRight = null;
    state.tapholeTouched = true;
    state.pwHandle = '';
    state.pwFinish = '';
    state.pwName = 'No Plug & Waste';
    state.pwVariantId = null;
    state.pwTouched = true;

    // Deselect benchtop-step selections (regular swatches + moulded cards).
    $qa('[data-option-group="benchtop"] .vb-swatch').forEach(function (s) {
      s.classList.remove('vb-swatch--active');
    });
    $qa('[data-option-group="benchtop-special"] .vb-card--top-type').forEach(function (c) {
      c.classList.remove('vb-card--active');
    });
    // Thickness selector lives inside the Benchtop step's content; the
    // step's Not Required state hides .vb-step__content (CSS), so the
    // selector is hidden automatically. No explicit hide needed.

    setStepNotRequired('benchtop', ['benchtop'], true);
    setStepNotRequired('hardware', ['hardware'], true);
    setStepNotRequired('basin', ['basin'], true);
    setStepNotRequired('taphole', ['taphole', 'taphole-double'], true);
    setStepNotRequired('plug-waste', ['plug-waste'], true);

    // Clear any sidebar values that were populated before Cabinet Only was
    // picked (e.g. the customer selected a benchtop then changed their
    // mind and picked Cabinet Only on a different size). Using
    // updateSummary(key, null) also strips the --filled class, which is
    // what makes the "Not Required" pill visible (filled rows hide pills).
    ['benchtop', 'hardware', 'basin', 'taphole', 'pw'].forEach(function (key) {
      updateSummary(key, null);
    });
    // Also blank thumbs so prior selections' images don't linger.
    ['benchtop', 'hardware', 'basin', 'taphole', 'taphole-double', 'pw'].forEach(function (key) {
      updateSidebarThumb(key, '');
    });
  }

  /** Apply the "Integrated Top" (Matte Ceramic / Imperial Top) UI state.
   *  Called from the bowl-card handler when the customer picks one of
   *  these cards. The integrated top includes a basin baked in, so
   *  skipBasin is set — but handle + taphole + P&W stay in play. The
   *  taphole step may be locked to a specific count/position per the
   *  `top_variants` config (Imperial = 0TH, Matte Ceramic = 3TH). */
  function enterIntegratedTopMode(topKey) {
    // Leave any prior Cabinet-Only UI state first so we don't double-up
    // the Not Required badges on Handle/Taphole (which Cabinet Only
    // skips but integrated tops don't).
    resetCabinetOnlyMode();

    state.topType = topKey;
    state.benchtopName = topKey;
    state.benchtopMaterial = null;
    state.skipBasin = true;
    state.skipHardware = false;
    state.skipTaphole = false;
    // skipBenchtop is set below from entry.skip_benchtop (Matte Ceramic = true,
    // Imperial Top = false since it offers two stone finishes).

    // Clear basin selections — the integrated basin is implicit.
    $qa('.vb-card--basin').forEach(function (c) { c.classList.remove('vb-card--active'); });
    state.basinKey = '';
    state.basinName = 'No Basin';
    state.basinHandle = '';
    state.basinVariantId = null;
    state.basinPrice = 0;
    state.basinFinish = null;
    state.basinTouched = true;

    // Deselect regular benchtop swatches / special top cards / thickness.
    $qa('[data-option-group="benchtop"] .vb-swatch').forEach(function (s) {
      s.classList.remove('vb-swatch--active');
    });
    $qa('[data-option-group="benchtop-special"] .vb-card--top-type').forEach(function (c) {
      c.classList.remove('vb-card--active');
    });
    // Imperial Top keeps Step 3 visible (for the Blossoming + Sakal
    // finish restriction), so the thickness selector inside Step 3
    // wouldn't be hidden by the Not Required collapse. Hide it
    // explicitly — Stone Thickness is a Statement/Royale upgrade for
    // regular Durasein finishes, not for integrated tops.
    var thicknessGrpHide = $q('[data-thickness-group]');
    if (thicknessGrpHide) thicknessGrpHide.classList.add('vb-thickness-selector--hidden');
    state.thickness = null;
    state.thicknessSurcharge = 0;

    // Basin step shows the integrated-basin message.
    var basinStep = $q('[data-conditional-step="basin"]');
    if (basinStep) {
      basinStep.classList.add('vb-basin-integrated');
      var msg = basinStep.querySelector('.vb-basin-included-msg');
      if (!msg) {
        msg = document.createElement('p');
        msg.className = 'vb-basin-included-msg';
        msg.style.cssText = 'padding: 1rem; background: #f5f5f5; border-radius: 8px; text-align: center; color: #666;';
        basinStep.querySelector('.vb-step__content').insertBefore(msg, basinStep.querySelector('.vb-step__content').firstChild);
      }
      msg.textContent = 'Basin is included with your selected ' + topKey + '.';
    }
    setStepNotRequired('basin', ['basin'], true);

    // Lock the taphole step per the top_variants config (Imperial = 0TH,
    // Matte Ceramic = 3TH). Read from VB_CONFIG.topVariants rather than
    // hardcoding.
    var entry = (VB_CONFIG.topVariants || []).find(function (e) { return e.key === topKey; });
    if (entry) {
      state._mouldedTapholeCode = entry.taphole_code;
      var tapholeStep = $q('[data-conditional-step="taphole"]');
      if (tapholeStep) {
        var noteEl = tapholeStep.querySelector('.vb-taphole-note');
        if (entry.taphole_note) {
          if (!noteEl) {
            noteEl = document.createElement('p');
            noteEl.className = 'vb-taphole-note';
            noteEl.style.cssText = 'padding: 0.75rem 1rem; background: #f5f5f5; border-radius: 8px; color: #666; font-size: 0.9em; margin-top: 0.5rem;';
            tapholeStep.querySelector('.vb-step__content').appendChild(noteEl);
          }
          noteEl.textContent = entry.taphole_note;
          noteEl.classList.remove('d-none');
          // The note is self-explanatory — hide the redundant taphole
          // cards so the customer isn't presented with a single locked
          // "No Taphole" choice next to the explanation.
          state.tapholeLocked = true;
        } else if (noteEl) {
          noteEl.classList.add('d-none');
          state.tapholeLocked = false;
        } else {
          state.tapholeLocked = false;
        }
      }
      // Imperial Top restricts the benchtop FINISH swatches (step 3) to
      // Blossoming + Sakal only. Apply that filter when the restriction
      // key is present in the config.
      applyBenchtopFinishRestriction(entry.finish_restriction, entry.finish_restriction_material);

      // Matte Ceramic has only one finish — collapse Step 3 entirely
      // (no swatches for the customer to pick). Imperial Top keeps Step 3
      // active, restricted to Blossoming + Sakal.
      state.skipBenchtop = !!entry.skip_benchtop;
      setStepNotRequired('benchtop', ['benchtop'], !!entry.skip_benchtop);
    }
  }

  /** Undo integrated-top mode. Clears taphole lock + benchtop finish
   *  restriction + basin-integrated UI. */
  function resetIntegratedTopMode() {
    // Differentiate from cabinet-only mode by skipHardware (integrated tops
    // never skip Handle/Taphole; cabinet-only does). skipBenchtop alone is
    // not a disqualifier — Matte Ceramic legitimately sets it.
    if (!state.skipBasin || state.skipHardware) return;
    state.skipBasin = false;
    state.skipBenchtop = false;
    state.tapholeLocked = false;
    state.basinTouched = false;
    state._mouldedTapholeCode = undefined;
    var basinStep = $q('[data-conditional-step="basin"]');
    if (basinStep) basinStep.classList.remove('vb-basin-integrated');
    setStepNotRequired('basin', ['basin'], false);
    setStepNotRequired('benchtop', ['benchtop'], false);
    var tapholeNote = $q('.vb-taphole-note');
    if (tapholeNote) tapholeNote.classList.add('d-none');
    applyBenchtopFinishRestriction(null, null);
    var thicknessGrpShow = $q('[data-thickness-group]');
    if (thicknessGrpShow) thicknessGrpShow.classList.remove('vb-thickness-selector--hidden');
  }

  /** Restrict benchtop swatches in Step 3 to a specific set of finish
   *  names and/or a specific material group. Pass null/null to clear.
   *  Also hides each material-group label (the .vb-step__label that
   *  immediately precedes a .vb-swatches container) when all of that
   *  group's swatch items end up hidden — otherwise customers see e.g.
   *  "Cherry Pie Premium Solid Surface — 20mm" floating above an empty
   *  space. */
  function applyBenchtopFinishRestriction(allowedNames, materialGroup) {
    $qa('[data-option-group="benchtop"] .vb-swatch-item').forEach(function (item) {
      var swatch = item.querySelector('.vb-swatch');
      if (!swatch) return;
      var name = swatch.dataset.benchtopName || '';
      var material = swatch.dataset.benchtopMaterial || '';
      var ok = true;
      if (allowedNames && allowedNames.length) {
        ok = allowedNames.indexOf(name) >= 0;
      }
      if (ok && materialGroup) {
        ok = material.toLowerCase().indexOf(materialGroup.toLowerCase()) >= 0;
      }
      item.classList.toggle('d-none', !ok);
    });
    // Hide group labels whose swatches are now all hidden.
    $qa('[data-option-group="benchtop"]').forEach(function (group) {
      var anyVisible = !!group.querySelector('.vb-swatch-item:not(.d-none)');
      var label = group.previousElementSibling;
      if (label && label.classList.contains('vb-step__label')) {
        label.classList.toggle('d-none', !anyVisible);
      }
    });
  }

  /** Undo any Cabinet-Only UI state — used before applying a new top
   *  option (regular benchtop swatch, moulded-top card) or before a
   *  regular bowl-card click when Cabinet Only was previously active. */
  function resetCabinetOnlyMode() {
    // Discriminate cabinet-only from integrated-top (Matte Ceramic) by
    // skipHardware — only cabinet-only flips Handle/Taphole/P&W to skip.
    if (!state.skipHardware) return;
    state.skipBenchtop = false;
    state.skipBasin = false;
    state.skipHardware = false;
    state.skipTaphole = false;
    state.basinTouched = false;
    state.tapholeTouched = false;
    state.pwTouched = false;
    setStepNotRequired('benchtop', ['benchtop'], false);
    setStepNotRequired('hardware', ['hardware'], false);
    setStepNotRequired('basin', ['basin'], false);
    setStepNotRequired('taphole', ['taphole', 'taphole-double'], false);
    setStepNotRequired('plug-waste', ['plug-waste'], false);
  }

  /** Filter benchtop swatches to only show colours for a specific material */
  function filterBenchtopsByMaterial(material) {
    var groups = $qa('[data-option-group="benchtop"]');
    groups.forEach(function (group) {
      var groupMaterial = group.dataset.material || '';
      if (!material) {
        group.classList.remove('d-none');
        var label = group.previousElementSibling;
        if (label && label.classList.contains('vb-step__label')) label.classList.remove('d-none');
      } else {
        var match = groupMaterial.toLowerCase().indexOf(material.toLowerCase()) !== -1;
        group.classList.toggle('d-none', !match);
        var label = group.previousElementSibling;
        if (label && label.classList.contains('vb-step__label')) label.classList.toggle('d-none', !match);
      }
    });
  }

  /** Smoothly collapse or expand the entire Moulded Tops section (label +
   *  cards grid + divider). Uses explicit pixel-height transitions measured
   *  off scrollHeight because CSS alone can't transition from auto → 0.
   *  Idempotent: re-calling with the same state is a no-op. */
  function setMouldedTopsCollapsed(hide) {
    var section = $q('.vb-moulded-tops-section');
    if (!section) return;
    var isHidden = section.classList.contains('vb-moulded-tops-section--hidden');
    if (hide === isHidden) return;

    // Clip while the height animates so content doesn't burst out of the
    // transitioning box. Removed after the expand settles so the stable
    // expanded state can render card shadows without clipping.
    section.style.overflow = 'hidden';

    if (hide) {
      section.style.height = section.scrollHeight + 'px';
      void section.offsetHeight;
      section.classList.add('vb-moulded-tops-section--hidden');
      section.style.height = '0px';
      // stays overflow:hidden via the --hidden class after inline style clears
    } else {
      section.classList.remove('vb-moulded-tops-section--hidden');
      section.style.height = '0px';
      void section.offsetHeight;
      var target = section.scrollHeight;
      section.style.height = target + 'px';
      setTimeout(function () {
        if (!section.classList.contains('vb-moulded-tops-section--hidden')) {
          section.style.height = '';
          section.style.overflow = ''; // allow shadows to escape when stable
        }
      }, 380);
    }
  }

  /** Update special top card visibility based on current size/bowl selection.
   *  Uses the Bowl Position pattern: snap all to hidden first, then stagger
   *  the applicable cards in. Avoids grid-reflow jolts from partial d-none
   *  toggles. Skips entirely if the applicable set hasn't changed, so
   *  unrelated state updates don't cause a flicker. */
  function updateTopTypePrices() {
    if (!hasTopTypes || !VB_CONFIG.variantLookup || !state.size || !state.bowl) return;
    var cards = $qa('[data-option-group="benchtop-special"] .vb-card--top-type');
    var sizeBowl = state.size + 'mm ' + state.bowl;

    // Build the current and next applicable sets to detect a no-op update.
    var prevKeys = [];
    var nextCards = [];
    cards.forEach(function (card) {
      var topKey = card.dataset.topKey;
      if (!card.classList.contains('d-none')) prevKeys.push(topKey);
      if (VB_CONFIG.variantLookup[sizeBowl + '|' + topKey]) nextCards.push(card);
    });
    var nextKeys = nextCards.map(function (c) { return c.dataset.topKey; });
    prevKeys.sort(); nextKeys.sort();
    var changed = prevKeys.join(',') !== nextKeys.join(',');

    // If the applicable set changed, restage cards FIRST so the section's
    // upcoming scrollHeight measurement reflects the final layout. Each
    // card is put into its "invisible starting" state via the --entering
    // class (opacity:0 held by the animation's 0% keyframe + fill-mode:both)
    // so it contributes layout but doesn't flash visually.
    if (changed) {
      cards.forEach(function (card) {
        card.classList.add('d-none');
        card.classList.remove('vb-card--entering', 'vb-card--leaving');
        card.style.animationDelay = '';
      });
      nextCards.forEach(function (card, i) {
        card.classList.remove('d-none');
        card.style.animationDelay = (i * 50) + 'ms';
        card.classList.add('vb-card--entering');
      });
    }

    // Now collapse/expand the section — scrollHeight includes revealed cards.
    setMouldedTopsCollapsed(nextCards.length === 0);

    if (changed && nextCards.length) {
      // Clean up the entering state after all staggered animations finish
      // (max stagger delay + animation duration + tiny safety margin).
      var maxDelay = (nextCards.length - 1) * 50 + 300;
      setTimeout(function () {
        nextCards.forEach(function (card) {
          card.classList.remove('vb-card--entering');
          card.style.animationDelay = '';
        });
      }, maxDelay + 50);
    }
  }

  // ── DOM refs ───────────────────────────────────────────────────
  const $id = (id) => document.getElementById(id);
  const $qa = (sel, root) => (root || builder).querySelectorAll(sel);
  const $q = (sel, root) => (root || builder).querySelector(sel);

  // ── Card entrance animation helper ────────────────────────────
  /** Animate cards appearing with staggered fade-up entrance.
   *  Removes d-none and applies staggered animation-delay per card.
   *  @param {NodeList|Array} cards - cards to animate in
   */
  function animateCardsIn(cards) {
    var cardArr = Array.from(cards);
    if (cardArr.length === 0) return;
    cardArr.forEach(function (card, i) {
      card.classList.remove('d-none');
      card.classList.remove('vb-card--entering');
      void card.offsetWidth;
      card.style.animationDelay = (i * 50) + 'ms';
      card.classList.add('vb-card--entering');
    });
    var maxDelay = (cardArr.length - 1) * 50 + 300;
    setTimeout(function () {
      cardArr.forEach(function (card) {
        card.classList.remove('vb-card--entering');
        card.style.animationDelay = '';
      });
    }, maxDelay + 50);
  }

  // ── Matrix data (loaded async) ────────────────────────────────
  let matrixData = null;

  // Single bowl / centre+double symmetric taphole codes
  // From ADP Basin Matrix and Taphole Key PDF
  const TAPHOLE_CODES = {
    '0': [],
    '1': ['9', '10', '12', '2', '3'],
    '2': ['12'],
    '3': ['10', '2'],
    '4': ['9', '3'],
    '5': ['10', '12', '2'],
    '6': ['9', '10', '2', '3'],
    '7': ['10'],
    '8': ['9'],
    '9': ['2'],
    '10': ['3'],
    '12': ['12', '2'],
    '13': ['10', '12'],
    '14': ['9', '10'],
    '15': ['2', '3'],
    '16': ['12', '2', '3'],
    '17': ['9', '10', '12'],
    '18': ['10', '12', '2', '3'],
    '19': ['9', '10', '12', '2'],
    '20': ['10', '2', '3'],
    '21': ['9', '10', '2'],
  };

  // Double bowl codes — each maps to separate LH/RH position arrays
  // These are independent codes, NOT compositions of two single codes
  const DOUBLE_TAPHOLE_CODES = {
    '77':   { left: ['10'],              right: ['2'] },
    '88':   { left: ['9'],               right: ['3'] },
    '99':   { left: ['2'],               right: ['10'] },
    '1010': { left: ['3'],               right: ['9'] },
    '1212': { left: ['12', '2'],         right: ['10', '12'] },
    '1313': { left: ['10', '12'],        right: ['12', '2'] },
    '1414': { left: ['9', '10'],         right: ['2', '3'] },
    '1515': { left: ['2', '3'],          right: ['9', '10'] },
    '1616': { left: ['12', '2', '3'],    right: ['9', '10', '12'] },
    '1717': { left: ['9', '10', '12'],   right: ['12', '2', '3'] },
    '1818': { left: ['10', '12', '2', '3'], right: ['9', '10', '12', '2'] },
    '1919': { left: ['9', '10', '12', '2'], right: ['10', '12', '2', '3'] },
    '2020': { left: ['10', '2', '3'],    right: ['9', '10', '3'] },
    '2121': { left: ['9', '10', '2'],    right: ['10', '2', '3'] },
  };

  // ── Taphole pair card helpers (double bowl) ────────────────────
  var TAPHOLE_DOT_COORDS = {
    '12': { cx: 50, cy: 5 },
    '10': { cx: 13, cy: 23 },
    '2':  { cx: 87, cy: 23 },
    '9':  { cx: 5,  cy: 50 },
    '3':  { cx: 95, cy: 50 },
  };

  // Mirror map for symmetric codes — defines natural paired positions
  var MIRROR_MAP = { '10': '2', '2': '10', '9': '3', '3': '9', '12': '12' };

  /** Build a basin SVG string with optional taphole dot.
   *  Uses viewBox -10 -10 120 120 to give room for dots/ripples near edges. */
  function buildBasinSvg(clockNum, uid) {
    var gid = 'tpg' + uid;
    var fid = 'tpf' + uid;
    var svg = '<svg class="vb-taphole-pair__svg" viewBox="-10 -10 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">'
      + '<defs><radialGradient id="' + gid + '" cx="50%" cy="42%" r="55%">'
      + '<stop offset="0%" stop-color="#fff"/><stop offset="50%" stop-color="#f5f5f5"/>'
      + '<stop offset="85%" stop-color="#e6e6e6"/><stop offset="100%" stop-color="#d9d9d9"/>'
      + '</radialGradient><filter id="' + fid + '"><feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#000" flood-opacity="0.12"/></filter></defs>'
      + '<circle cx="50" cy="50" r="38" fill="#e0e0e0" filter="url(#' + fid + ')"/>'
      + '<circle cx="50" cy="50" r="36" fill="#eee"/>'
      + '<circle cx="50" cy="50" r="32" fill="url(#' + gid + ')"/>'
      + '<circle cx="50" cy="50" r="5.5" fill="#999"/>';
    if (clockNum && TAPHOLE_DOT_COORDS[clockNum]) {
      var d = TAPHOLE_DOT_COORDS[clockNum];
      svg += '<circle class="vb-taphole-ripple" cx="' + d.cx + '" cy="' + d.cy + '" r="5" fill="none" stroke="#c41230" stroke-width="1.5"/>';
      svg += '<circle class="vb-taphole-dot" cx="' + d.cx + '" cy="' + d.cy + '" r="5" fill="#c41230"/>';
    }
    svg += '</svg>';
    return svg;
  }

  /** Build a rectangular "Taphole in Basin" SVG */
  function buildInBasinSvg(uid) {
    var gid = 'tpib' + uid;
    return '<svg class="vb-taphole-pair__svg" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">'
      + '<defs><linearGradient id="' + gid + '" x1="0%" y1="50%" x2="100%" y2="50%">'
      + '<stop offset="0%" stop-color="#ddd"/><stop offset="25%" stop-color="#f0f0f0"/>'
      + '<stop offset="50%" stop-color="#f8f8f8"/><stop offset="75%" stop-color="#f0f0f0"/>'
      + '<stop offset="100%" stop-color="#ddd"/></linearGradient></defs>'
      + '<rect x="8" y="12" width="84" height="76" rx="6" fill="#e0e0e0"/>'
      + '<rect x="10" y="14" width="80" height="72" rx="5" fill="#eee"/>'
      + '<rect x="14" y="30" width="72" height="52" rx="4" fill="url(#' + gid + ')"/>'
      + '<circle cx="50" cy="58" r="5.5" fill="#999"/>'
      + '<circle cx="50" cy="21" r="5" fill="#c41230"/>'
      + '</svg>';
  }

  /** Format a clock position for display */
  function formatPos(val) {
    if (!val || val === 'No Taphole') return 'No Taphole';
    if (val === 'Taphole in Basin') return 'Taphole in Basin';
    return val + " O'Clock";
  }

  /** Generate pair combinations from a matrix taphole code */
  function generateTapholePairs(code) {
    var pairs = [];

    // Always add "No Taphole" first
    pairs.push({ left: null, right: null, label: 'No Taphole' });

    if (!code || code === 'X') return pairs;
    if (code === '0') {
      pairs[0].autoSelect = true;
      return pairs;
    }
    if (code === '*') {
      return [{ left: 'inBasin', right: 'inBasin', label: 'Taphole in Basin', autoSelect: true }];
    }

    var codeNum = parseInt(code, 10);
    var leftPositions, rightPositions;

    if (codeNum > 21 && DOUBLE_TAPHOLE_CODES[code]) {
      // Double-specific code — use defined left/right arrays
      var dbl = DOUBLE_TAPHOLE_CODES[code];
      leftPositions = dbl.left;
      rightPositions = dbl.right;
      // Cartesian product — all combinations
      leftPositions.forEach(function (l) {
        rightPositions.forEach(function (r) {
          var label = l === r
            ? "Both: " + formatPos(l)
            : "Left: " + formatPos(l) + "  \u00b7  Right: " + formatPos(r);
          pairs.push({ left: l, right: r, label: label });
        });
      });
    } else {
      // Symmetric code — show mirrored pairs only
      var positions = TAPHOLE_CODES[code] || [];
      if (positions.length === 0) {
        pairs[0].autoSelect = true;
        return pairs;
      }
      var added = {};
      // First pass: mirrored pairs (10↔2, 9↔3, 12↔12)
      positions.forEach(function (p) {
        var mirror = MIRROR_MAP[p];
        if (mirror && positions.indexOf(mirror) !== -1) {
          var key = [p, mirror].sort().join('-');
          if (!added[key]) {
            added[key] = true;
            var label = p === mirror
              ? "Both: " + formatPos(p)
              : "Left: " + formatPos(p) + "  \u00b7  Right: " + formatPos(mirror);
            pairs.push({ left: p, right: mirror, label: label });
          }
        }
      });
    }

    return pairs;
  }

  /** Render pair cards into the container and attach click handlers */
  function renderTapholePairCards(container, pairs) {
    var checkSvg = '<svg width="20" height="20" viewBox="0 0 20 20" fill="none">'
      + '<circle cx="10" cy="10" r="10" fill="#c41230"/>'
      + '<path d="M6 10l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    pairs.forEach(function (pair, idx) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'vb-card vb-card--taphole-pair';
      btn.dataset.tapholeLeft = pair.left ? formatPos(pair.left) : 'No Taphole';
      btn.dataset.tapholeRight = pair.right ? formatPos(pair.right) : 'No Taphole';
      btn.setAttribute('aria-label', pair.label);

      var leftSvg, rightSvg;
      if (pair.left === 'inBasin') {
        leftSvg = buildInBasinSvg(idx + 'L');
        rightSvg = buildInBasinSvg(idx + 'R');
      } else {
        leftSvg = buildBasinSvg(pair.left, idx + 'L');
        rightSvg = buildBasinSvg(pair.right, idx + 'R');
      }

      var leftPosText = pair.left === 'inBasin' ? 'In Basin' : (pair.left ? formatPos(pair.left) : 'No Taphole');
      var rightPosText = pair.right === 'inBasin' ? 'In Basin' : (pair.right ? formatPos(pair.right) : 'No Taphole');

      btn.innerHTML = '<div class="vb-taphole-pair">'
        + '<div class="vb-taphole-pair__side"><span class="vb-taphole-pair__label">Left</span>' + leftSvg + '<span class="vb-taphole-pair__pos">' + leftPosText + '</span></div>'
        + '<div class="vb-taphole-pair__divider"></div>'
        + '<div class="vb-taphole-pair__side"><span class="vb-taphole-pair__label">Right</span>' + rightSvg + '<span class="vb-taphole-pair__pos">' + rightPosText + '</span></div>'
        + '</div>'
        + '<span class="vb-card__check">' + checkSvg + '</span>';

      btn.addEventListener('click', function () {
        container.querySelectorAll('.vb-card--taphole-pair').forEach(function (c) {
          c.classList.remove('vb-card--active');
        });
        btn.classList.add('vb-card--active');
        state.tapholeLeft = btn.dataset.tapholeLeft;
        state.tapholeRight = btn.dataset.tapholeRight;
        state.tapholeTouched = true;
        updateDoubleTapholePairSummary(state.tapholeLeft, state.tapholeRight);
        updateATCState();
      });

      container.appendChild(btn);
    });

    // Staggered entrance animation for taphole pair cards
    animateCardsIn(container.querySelectorAll('.vb-card--taphole-pair'));
  }

  /** Update double bowl sidebar with combined pair description */
  function updateDoubleTapholePairSummary(leftVal, rightVal) {
    var label = '';
    if (leftVal && rightVal) {
      if (leftVal === 'No Taphole' && rightVal === 'No Taphole') {
        label = 'No Taphole';
      } else if (leftVal === 'Taphole in Basin') {
        label = 'Taphole in Basin';
      } else if (leftVal === rightVal) {
        label = 'Both: ' + leftVal;
      } else {
        label = 'L: ' + leftVal + ' / R: ' + rightVal;
      }
    }
    var ids = ['vb-sum-taphole-double', 'vb-sum-taphole-double-m'];
    var isOptedOut = label === 'No Taphole' && !state.basinKey;
    var isFilled = !!label && !isOptedOut;
    ids.forEach(function (id) {
      var el = $id(id);
      if (el) {
        el.textContent = label;
        var item = el.closest('.vb-sidebar__item');
        if (item) {
          item.classList.toggle('vb-sidebar__item--filled', isFilled);
          item.classList.toggle('vb-sidebar__item--opted-out', isOptedOut);
        }
      }
    });
  }

  // ── Dynamic sidebar render image ───────────────────────────────
  var FINISH_TO_SLUG = VB_CONFIG.finishToSlug || {
    'Ultra White Matte': 'ultra-white',
    'Coastal Oak Woodmatt': 'coastal-oak',
    'Prime Oak Woodmatt': 'prime-oak',
    'Tasmanian Oak Woodmatt': 'tasmanian-oak',
    'Florentine Walnut Woodmatt': 'florentine-walnut',
    'Bottega Oak Woodmatt': 'bottega-oak',
  };

  function getRenderConfig() {
    if (!state.size || !state.bowl) return null;
    var bowl = state.bowl.toLowerCase();
    var letter = '';
    if (bowl.includes('centre') || bowl.includes('center')) letter = 'c';
    else if (bowl.includes('left')) letter = 'l';
    else if (bowl.includes('right')) letter = 'r';
    else if (bowl.includes('double')) letter = 'd';
    // 750 and 900 centre don't use a letter suffix in filenames
    if ((state.size === '750' || state.size === '900') && letter === 'c') {
      return state.size === '750' ? '750' : '900c';
    }
    return state.size + letter;
  }

  function updateSidebarRender() {
    var config = getRenderConfig();
    var finishSlug = state.finish ? FINISH_TO_SLUG[state.finish] : null;
    if (!config || !finishSlug) return;

    var imagePrefix = VB_CONFIG.imagePrefix || 'waverley';
    var filename = 'vb-' + imagePrefix + '-' + finishSlug + '-' + config + '.png';
    var filesBase = builder.dataset.filesBase || '';
    var url = filesBase + filename;

    ['vb-sidebar-img', 'vb-sidebar-img-mobile'].forEach(function (id) {
      var img = $id(id);
      if (!img) return;
      // Only swap if URL actually changed
      if (img.src.indexOf(filename) !== -1) return;
      // Crossfade
      img.style.transition = 'opacity 0.3s ease';
      img.style.opacity = '0';
      setTimeout(function () {
        img.src = url;
        img.onload = function () {
          img.style.opacity = '1';
        };
        // Fallback in case image doesn't load
        img.onerror = function () {
          img.style.opacity = '1';
        };
      }, 300);
    });
  }

  // Load matrix JSON — prefer inline metafield data, fall back to async fetch
  (function loadMatrix() {
    var inlineEl = document.getElementById('vb-matrix-data');
    if (inlineEl) {
      try {
        matrixData = JSON.parse(inlineEl.textContent);
        if (state.size && state.bowl) filterBasinsByConfig();
        return;
      } catch (e) { console.warn('Failed to parse inline matrix:', e); }
    }
    var url = builder.dataset.matrixUrl;
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        matrixData = data;
        if (state.size && state.bowl) filterBasinsByConfig();
      })
      .catch(function (err) { console.warn('Vanity matrix not loaded:', err); });
  })();

  // Finish name to swatch colour mapping — declared up-here (above
  // loadFinishes) because addFinishBadges() references it and loadFinishes
  // calls addFinishBadges synchronously when inline data is present. With
  // the previous declaration further down (after loadFinishes), `var`
  // hoisting made FINISH_COLOURS undefined at the moment addFinishBadges
  // ran, throwing a TypeError on every page load that was masked by the
  // async fallback fetch path. Moved up here to fix.
  var FINISH_COLOURS = VB_CONFIG.basinFinishColours || {
    'Gloss White':       { bg: '#ffffff', border: '#ddd' },
    'Matte White':       { bg: '#f0eded', border: '#ddd' },
    'Sea Salt':          { bg: '#f0edea', border: '#ddd' },
    'Matte Black':       { bg: '#1a1a1a', border: '#1a1a1a' },
    'Black':             { bg: '#2a2a2a', border: '#2a2a2a' },
    'Black Out/White In':{ bg: '#1a1a1a', border: '#1a1a1a', half: '#ffffff' },
    'Butter':            { bg: '#e8e0c8', border: '#d4cbb0' },
    'Plum':              { bg: '#a89080', border: '#96806f' },
    'Matte Ivory':       { bg: '#d5cdb8', border: '#c4bca7' },
    'Matte Olive':       { bg: '#9a9478', border: '#8a8468' },
    'Matte Forest':      { bg: '#464a44', border: '#363a34' },
    'Matte Concrete':    { bg: '#bcb8b0', border: '#aaa69e' },
    'Cape Verde':        { bg: '#4a5c52', border: '#3a4c42' },
    'Manyana':           { bg: '#8a7e74', border: '#7a6e64' },
    'Royal Beige':       { bg: '#c4b8a8', border: '#b4a898' },
    'Travertine':        { bg: '#ddd4c8', border: '#cdc4b8' },
  };

  // Load basin finishes — prefer inline metafield data, fall back to async fetch.
  // After the base data loads, kick off a live price/availability patch from
  // the Shopify collection endpoint so prices reflect current admin state and
  // discontinued basins (archived, unpublished, or out-of-stock) disappear.
  let finishesData = null;
  (function loadFinishes() {
    // Basin-less vanities (Zurich family, Allie, other small-space) render
    // no basin UI, so the finishes JSON and the 5-page live-price patch are
    // dead weight - 6 wasted requests per builder page view. Skip the whole
    // load unless a basin step exists. Glacier is exempted explicitly: its
    // basin flow lives in vanity-builder-glacier.js without the
    // data-conditional-step="basin" marker, so it loads as before.
    if (!hasBasinStep && builder.dataset.productHandle !== 'glacier-full-depth') return;
    var inlineEl = document.getElementById('vb-finishes-data');
    if (inlineEl) {
      try {
        finishesData = JSON.parse(inlineEl.textContent);
        addFinishBadges();
        patchLiveBasinPrices();
        return;
      } catch (e) { console.warn('Failed to parse inline finishes:', e); }
    }
    var url = builder.dataset.finishesUrl;
    if (!url) return;
    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        finishesData = data;
        addFinishBadges();
        patchLiveBasinPrices();
      })
      .catch(function (err) { console.warn('Basin finishes not loaded:', err); });
  })();

  /** Fetch live Shopify data for basin products and patch prices, variant IDs,
   *  and availability onto finishesData. Finishes that are unavailable (archived
   *  / unpublished / marked unavailableForSale) are removed. Basins with zero
   *  remaining finishes have their cards removed from the DOM so customers
   *  can't see discontinued options. Names, thumbnails, overlay images, and
   *  finish labels are untouched — they stay from the static JSON / metaobjects. */
  function patchLiveBasinPrices() {
    if (!finishesData || typeof finishesData !== 'object') return;
    var endpoint = '/collections/adp-bathroom-basins?view=vb-data';
    var pageNumbers = [1, 2, 3, 4, 5]; // capacity 250 basins; we currently have ~120
    // Per-page fetches are resilient: any single page failure returns an
    // empty result so the rest of the patch still runs.
    var pageFetches = pageNumbers.map(function (p) {
      return fetch(endpoint + '&page=' + p, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          try { return JSON.parse(t); }
          catch (e) { return { products: [] }; }
        })
        .catch(function () { return { products: [] }; });
    });
    Promise.all(pageFetches)
      .then(function (pages) {
        // Build handle -> {price, variant_id, available} lookup
        var liveByHandle = {};
        pages.forEach(function (page) {
          (page.products || []).forEach(function (prod) {
            liveByHandle[prod.handle] = prod;
          });
        });
        if (Object.keys(liveByHandle).length === 0) {
          console.warn('[VB] Live basin fetch returned zero products — keeping static prices.');
          return;
        }

        // Patch each basin's finishes: drop any that aren't in live data or
        // are marked unavailable, update price + variant_id on the rest.
        Object.keys(finishesData).forEach(function (basinKey) {
          var entry = finishesData[basinKey];
          if (!entry || !entry.finishes) return;
          var patched = [];
          entry.finishes.forEach(function (f) {
            var live = liveByHandle[f.handle];
            if (!live || live.available === false) return;
            f.price = Number(live.price).toFixed(2);
            f.compare_at_price = (live.compare_at_price != null)
              ? Number(live.compare_at_price).toFixed(2)
              : null;
            f.variant_id = live.variant_id;
            patched.push(f);
          });
          entry.finishes = patched;
        });

        // Refresh "From $X" badges with live prices
        addFinishBadges();

        // Iterate every rendered basin card and remove it if NONE of its
        // finish handles resolve to an available live product. This catches:
        //   - cards whose basin has all finishes unavailable (patched above)
        //   - cards rendered from a metaobject that never had a static JSON
        //     entry (e.g. discontinued basins left in compatible_basins lists)
        //   - any future discontinuation without requiring manual cleanup
        var allBasinCards = document.querySelectorAll('.vb-card--basin[data-basin-handles]');
        allBasinCards.forEach(function (card) {
          var handlesAttr = card.dataset.basinHandles || '';
          if (!handlesAttr) return;
          var handles = handlesAttr.split(',')
            .map(function (h) { return h.trim(); })
            .filter(Boolean);
          if (!handles.length) return;
          var anyAvailable = handles.some(function (h) {
            var live = liveByHandle[h];
            return live && live.available !== false;
          });
          if (anyAvailable) return;
          if (card.parentNode) card.parentNode.removeChild(card);
          var key = card.dataset.basinKey;
          if (key) delete finishesData[key];
        });
      })
      .catch(function (err) {
        // Non-fatal: if the live fetch fails (network issue, Shopify down),
        // the static finishesData is already loaded and the VB works normally.
        console.warn('[VB] Live basin price patch failed — using static prices:', err);
      });
  }

  /** Inject (or remove) a strikethrough RRP span next to a basin card's
   *  price chip. Mirrors the .vb-card__price-was treatment on Step 1
   *  vanity cards. Idempotent — safe to call repeatedly. */
  function injectBasinStrikethrough(priceEl, compareAtPrice, price) {
    if (!priceEl) return;
    var existing = priceEl.querySelector('.vb-card__price-was');
    var cap = compareAtPrice != null ? parseFloat(compareAtPrice) : 0;
    var p = price != null ? parseFloat(price) : 0;
    if (cap > p && p > 0) {
      var span = existing || document.createElement('span');
      span.className = 'vb-card__price-was';
      span.textContent = '$' + Math.round(cap);
      if (!existing) priceEl.appendChild(span);
    } else if (existing) {
      existing.remove();
    }
  }

  /** Add finish swatch dots to basin cards */
  function addFinishBadges() {
    if (!finishesData) return;
    var cards = $qa('[data-option-group="basin"] .vb-card--basin');
    cards.forEach(function (card) {
      var key = card.dataset.basinKey;
      if (!key || !finishesData[key]) return;
      var finishes = finishesData[key].finishes;

      // Add price for single-finish basins and set thumb image
      if (finishes.length === 1) {
        var priceEl = card.querySelector('.vb-card__price');
        if (!priceEl) {
          priceEl = document.createElement('span');
          priceEl.className = 'vb-card__price';
          card.insertBefore(priceEl, card.firstChild);
        }
        var f0 = finishes[0];
        priceEl.textContent = '$' + Math.round(parseFloat(f0.price));
        injectBasinStrikethrough(priceEl, f0.compare_at_price, f0.price);
        var singleImg = card.querySelector('.vb-card__img');
        var singleThumb = f0.thumb ? (builder.dataset.assetBase || '') + f0.thumb : null;
        if (singleImg && singleThumb) {
          singleImg.src = singleThumb;
          card.dataset.originalImgSrc = singleThumb;
        }
        return;
      }

      // Sort finishes same way as overlay (price asc, standard first)
      var standardFinishOrder = VB_CONFIG.standardFinishSortOrder || ['Gloss White', 'Matte White', 'Matte Black', 'Black Out/White In'];
      var sortedForCard = finishes.slice().sort(function (a, b) {
        var pd = parseFloat(a.price) - parseFloat(b.price);
        if (pd !== 0) return pd;
        var aS = standardFinishOrder.indexOf(a.finish);
        var bS = standardFinishOrder.indexOf(b.finish);
        return (aS >= 0 ? aS : 100) - (bS >= 0 ? bS : 100);
      });

      // Set card image to the first finish's theme asset thumbnail
      var cardImg = card.querySelector('.vb-card__img');
      var assetBase = builder.dataset.assetBase || '';
      var firstThumb = sortedForCard[0] && sortedForCard[0].thumb ? assetBase + sortedForCard[0].thumb : null;
      if (cardImg && firstThumb) {
        if (!card.dataset.originalImgSrc) {
          card.dataset.originalImgSrc = firstThumb;
        }
        cardImg.src = firstThumb;
      } else if (cardImg && !card.dataset.originalImgSrc) {
        card.dataset.originalImgSrc = cardImg.src;
      }

      // Add price badge (top-left overlay)
      var prices = finishes.map(function (f) { return parseFloat(f.price); });
      var minPrice = Math.min.apply(null, prices);
      var maxPrice = Math.max.apply(null, prices);
      var priceEl = card.querySelector('.vb-card__price');
      if (!priceEl) {
        priceEl = document.createElement('span');
        priceEl.className = 'vb-card__price';
        card.insertBefore(priceEl, card.firstChild);
      }
      if (minPrice === maxPrice) {
        priceEl.textContent = '$' + Math.round(minPrice);
      } else {
        priceEl.textContent = 'From $' + Math.round(minPrice);
      }

      // Strikethrough RRP next to the chip when on sale. Use the first
      // sorted finish's compare_at_price (cheapest finish) since the chip
      // shows that finish's price (or "From $X" using its price).
      var primary = sortedForCard[0];
      injectBasinStrikethrough(priceEl, primary && primary.compare_at_price, primary && primary.price);

      // Remove old badges if any
      var old = card.querySelector('.vb-card__swatches');
      if (old) old.remove();

      var wrapper = document.createElement('div');
      wrapper.className = 'vb-card__swatches';

      sortedForCard.forEach(function (f) {
        var dot = document.createElement('span');
        dot.className = 'vb-card__swatch-dot';
        var colours = FINISH_COLOURS[f.finish] || { bg: '#ccc', border: '#bbb' };
        dot.style.backgroundColor = colours.bg;
        dot.style.borderColor = colours.border;
        dot.title = f.finish;

        // Half-and-half for Duo
        if (colours.half) {
          dot.style.background = 'linear-gradient(135deg, ' + colours.bg + ' 50%, ' + colours.half + ' 50%)';
        }

        wrapper.appendChild(dot);
      });

      card.appendChild(wrapper);
    });

    // Preload all finish thumbnail images into browser cache
    var assetBase = builder.dataset.assetBase || '';
    for (var key in finishesData) {
      finishesData[key].finishes.forEach(function (f) {
        if (f.thumb) {
          var img = new Image();
          img.src = assetBase + f.thumb;
        }
      });
    }
  }

  // ── Sticky sidebar offset ────────────────────────────────────
  (function setStickyOffset() {
    const sidebar = builder.querySelector('.vb-sidebar-wrapper');
    if (!sidebar) return;
    const header = document.querySelector('.section-header, .shopify-section-header, header');
    if (header) {
      const offset = header.getBoundingClientRect().bottom + 16;
      sidebar.style.top = offset + 'px';
      sidebar.style.maxHeight = 'calc(100vh - ' + (offset + 20) + 'px)';
    }
  })();

  // Summary elements (desktop + mobile)
  const summaryIds = {
    size: ['vb-sum-size', 'vb-sum-size-m'],
    bowl: ['vb-sum-bowl', 'vb-sum-bowl-m'],
    finish: ['vb-sum-finish', 'vb-sum-finish-m'],
    benchtop: ['vb-sum-benchtop', 'vb-sum-benchtop-m'],
    hardware: ['vb-sum-hardware', 'vb-sum-hardware-m'],
    sku: ['vb-sum-sku', 'vb-sum-sku-m'],
    basin: ['vb-sum-basin', 'vb-sum-basin-m'],
    pw: ['vb-sum-pw', 'vb-sum-pw-m'],
    taphole: ['vb-sum-taphole', 'vb-sum-taphole-m'],
  };

  // ── Helpers ────────────────────────────────────────────────────
  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ── Price counter animation ──────────────────────────────────
  var _priceAnimId = null;
  var _displayedPriceCents = 0;
  var _lastSettledPrice = 0;

  /** Format a +/- dollar delta like "+$500" or "−$50" (no cents, rounded). */
  function formatDelta(cents) {
    var abs = Math.abs(cents);
    var dollars = Math.round(abs / 100);
    var sign = cents > 0 ? '+' : '\u2212'; // proper minus sign, not hyphen
    return sign + '$' + dollars.toLocaleString();
  }

  /** Spawn a floating "+$X" / "−$X" indicator above each visible price value.
   * The host element is the nearest positioned ancestor that is NOT clipped
   * by `overflow: hidden` (so the badge can escape above its container). */
  function showPriceDelta(deltaCents) {
    if (!deltaCents) return;
    var targets = [
      { anchor: $id('vb-sticky-total'), host: document.querySelector('.vb-sticky-bar') },
      { anchor: $id('vb-price'),        host: null /* use parentNode */ },
      { anchor: $id('vb-price-m'),      host: null /* use parentNode */ },
    ];
    targets.forEach(function (t) {
      if (!t.anchor) return;
      // Skip prices that aren't currently rendered (desktop vs mobile toggle)
      if (t.anchor.offsetParent === null) return;
      var host = t.host || t.anchor.parentNode;
      if (!host) return;
      var prior = host.querySelector('.vb-price-delta');
      if (prior) prior.remove();
      var delta = document.createElement('span');
      delta.className = 'vb-price-delta ' + (deltaCents > 0 ? 'vb-price-delta--up' : 'vb-price-delta--down');
      delta.textContent = formatDelta(deltaCents);
      host.appendChild(delta);
      setTimeout(function () { if (delta.parentNode) delta.remove(); }, 1350);
    });
  }

  function animatePrice(toCents, duration) {
    if (_priceAnimId) cancelAnimationFrame(_priceAnimId);
    var fromCents = _displayedPriceCents;
    var priceIds = ['vb-price', 'vb-price-m', 'vb-sticky-total'];

    if (fromCents === toCents) {
      // No animation needed — show the accurate cents value (matches
      // checkout, including Caesarstone's fractional-cent variants).
      priceIds.forEach(function (id) {
        var el = $id(id);
        if (el) el.textContent = toCents ? formatMoney(toCents) : '--';
      });
      return;
    }

    var startTime = null;
    function easeOutQuad(t) { return t * (2 - t); }

    function tick(timestamp) {
      if (!startTime) startTime = timestamp;
      var elapsed = timestamp - startTime;
      var progress = Math.min(elapsed / duration, 1);
      var current = Math.round(fromCents + (toCents - fromCents) * easeOutQuad(progress));
      _displayedPriceCents = current;

      if (progress < 1) {
        // Mid-animation: round to whole dollars so the counter doesn't
        // flicker through fractional cents while interpolating.
        var displayCents = Math.round(current / 100) * 100;
        priceIds.forEach(function (id) {
          var el = $id(id);
          if (el) el.textContent = displayCents ? formatMoney(displayCents) : '--';
        });
        _priceAnimId = requestAnimationFrame(tick);
      } else {
        // Settle on the accurate value so the displayed price matches
        // what the customer pays at checkout.
        priceIds.forEach(function (id) {
          var el = $id(id);
          if (el) el.textContent = toCents ? formatMoney(toCents) : '--';
        });
        _priceAnimId = null;
      }
    }

    _priceAnimId = requestAnimationFrame(tick);
  }

  /** Update sidebar thumbnail for a given key */
  function updateSidebarThumb(key, src) {
    ['vb-thumb-' + key, 'vb-thumb-' + key + '-m'].forEach(function (id) {
      var img = $id(id);
      if (img) img.src = src || '';
    });
  }

  function setActive(group, el) {
    group.forEach((btn) => btn.classList.remove('vb-card--active', 'vb-swatch--active'));
    if (el) {
      var cls = el.classList.contains('vb-swatch') ? 'vb-swatch--active' : 'vb-card--active';
      // Force reflow to restart CSS animations when re-selecting same card
      void el.offsetWidth;
      el.classList.add(cls);
    }
  }

  function updateSummary(key, value) {
    // "No Taphole" reads as an opt-out only on basin-driven vanities (no
    // basin selected = nothing to drill). On fixed-choice vanities (Zurich
    // family) it is a deliberate required pick - a no-taphole top - and
    // renders as a normal filled selection.
    var isOptedOut = value === 'No Basin' || value === 'No Plug & Waste'
      || (key === 'taphole' && value === 'No Taphole' && !state.basinKey && !hasFixedTapholeChoice);
    var isFilled = !!value && !isOptedOut;
    summaryIds[key].forEach((id) => {
      const el = $id(id);
      if (el) {
        el.textContent = value || '';
        // Toggle filled state on parent item
        var item = el.closest('.vb-sidebar__item');
        if (item) {
          item.classList.toggle('vb-sidebar__item--filled', isFilled);
          item.classList.toggle('vb-sidebar__item--opted-out', isOptedOut);
          // Show/hide remove button for optional items
          var removeBtn = item.querySelector('.vb-sidebar__item-remove');
          if (removeBtn) removeBtn.classList.toggle('d-none', !isFilled);
        }
      }
    });
  }

  function updatePrice() {
    const priceIds = ['vb-price', 'vb-price-m', 'vb-sticky-total'];

    // Total = vanity price + thickness surcharge + basin price (x2 for double bowl)
    // + premium handle surcharge + plug & waste surcharge.
    // Premium handles and non-Chrome P&W both trigger Cart Transform reprices
    // ($20/handle and $20/P&W-unit respectively) — mirror those here so the
    // displayed total matches what the customer will actually pay.
    var basinQty = state.isDouble ? 2 : 1;
    // Per-vanity override: when VB_CONFIG.premiumHandlePricing === 'included'
    // (Tier A/C vanities — Surry, Thea, London, Waverley, Archie), premium
    // handles are free per ADP's pricing rule. Cart Transform mirrors this
    // via the '_Premium handles included' line-item property set in addToCart().
    //
    // Glacier (premiumHandlePricing === 'variant') reads the selected Tier
    // at runtime: Pro -> premium handles included free; Lite -> +$20/handle.
    var premiumHandleSurcharge = 0;
    if (state.handleCategory === 'premium') {
      if (VB_CONFIG.premiumHandlePricing === 'included') {
        premiumHandleSurcharge = 0;
      } else if (VB_CONFIG.premiumHandlePricing === 'variant') {
        var glacierTier = (window.VBGlacier && window.VBGlacier.state)
          ? window.VBGlacier.state.tier
          : null;
        premiumHandleSurcharge = (glacierTier === 'Pro') ? 0 : getHandleCount() * 2000;
      } else {
        premiumHandleSurcharge = getHandleCount() * 2000; // $20 × handle count, in cents
      }
    }
    // Chrome P&W is included free; non-Chrome adds $20 per bowl position.
    var pwSurcharge = (state.pwVariantId && state.pwFinish && state.pwFinish !== 'Chrome')
      ? basinQty * 2000
      : 0;
    var totalPrice = (state.variantPrice || 0)
      + (state.thicknessSurcharge || 0)
      + (state.basinPrice || 0) * basinQty
      + premiumHandleSurcharge
      + pwSurcharge;

    // Keep the handle "xN" badge in sync with size/cabinet changes.
    updateHandleQtyBadge();

    // Float a "+$X" or "−$X" delta indicator when the total changes. Only
    // fires after the first settled price (skips the 0 → first-selection jump),
    // and only for changes of at least $1 to avoid rounding noise.
    if (_lastSettledPrice > 0 && totalPrice !== _lastSettledPrice) {
      var deltaCents = totalPrice - _lastSettledPrice;
      if (Math.abs(deltaCents) >= 100) showPriceDelta(deltaCents);
    }
    _lastSettledPrice = totalPrice;

    // Animate price change instead of instant swap
    animatePrice(totalPrice || 0, 400);

    // Sale state derived from the vanity variant's compare_at_price. With
    // a site-wide sale, basins/handles/P&W are also discounted but their
    // compare-prices aren't yet in JS state — so the % shown is computed from
    // the vanity portion (which matches the advertised %, since the same
    // discount rate is applied across all VB products).
    var hasSale = state.variantComparePrice && state.variantComparePrice > state.variantPrice;

    // Hide the legacy "You save" row — Step 1 cards show per-variant
    // strikethrough/RRP, so the sidebar just needs the total + the % badge.
    var savingsRowIds = ['vb-savings-row', 'vb-savings-row-m'];
    savingsRowIds.forEach(function (id) {
      var el = $id(id);
      if (el) el.classList.add('d-none');
    });

    // "X% OFF" pill next to the total.
    var badgeIds = ['vb-discount-badge', 'vb-discount-badge-m'];
    badgeIds.forEach(function (id) {
      var el = $id(id);
      if (!el) return;
      if (hasSale) {
        var savings = state.variantComparePrice - state.variantPrice;
        var pct = Math.round(savings * 100 / state.variantComparePrice);
        el.textContent = pct + '% OFF';
        el.classList.remove('d-none');
      } else {
        el.classList.add('d-none');
      }
    });
  }

  function updateATCState() {
    // Keep the Drawer Side step + sidebar row in sync here - every
    // selection path (size cards, bowl overlay, express cards and both
    // express exits) already funnels through updateATCState, so this is
    // the one chokepoint that sees all size/dispatch changes.
    if (hasDrawerFeature) updateDrawerSideUI();
    if (hasHingingFeature) updateHingingUI();
    const hardwareReady = (hasHardwareStep && !state.skipHardware)
      ? (state.hardware && (state.hardwareType === 'push-to-open' || state.hardwareType === 'finger-pull' || state.handleFinish))
      : true;
    const benchtopReady = hasBenchtopStep ? !!state.benchtopName : true;
    const hingeReady = hasHingeStep ? !!state.hinge : true;
    // Multi-option fixed-taphole vanities (Zurich family) make the taphole
    // a required pick; basin-driven vanities keep it optional (no pick just
    // means no taphole). Express locks pre-set state.taphole, so express
    // builds pass automatically.
    const tapholeChoiceReady = hasFixedTapholeChoice ? !!state.taphole : true;
    // Drawer-side vanities: null only while a choice size awaits the pick
    // (before any size is selected, variantId already gates readiness).
    const drawerReady = hasDrawerFeature ? !!resolveDrawerSide() : true;
    // Hinging products: same shape - Standard derives everywhere except
    // the choice sizes, which need the customer's pick.
    const hingingReady = hasHingingFeature ? !!resolveHinging() : true;
    // Sold-out guard: a resolved variant that Shopify reports unavailable
    // (tracked, 0 stock, DENY) would error at /cart/add - lock the button
    // and say why instead. Undefined variantAvailable counts as available
    // (untracked vanities never set it false).
    const outOfStock = !!state.variantId && state.variantAvailable === false;
    const ready = state.variantId && state.finish && benchtopReady && hardwareReady && hingeReady && tapholeChoiceReady && drawerReady && hingingReady && !outOfStock;
    ['vb-atc-btn', 'vb-atc-btn-m'].forEach((id) => {
      const btn = $id(id);
      if (!btn) return;
      btn.disabled = !ready;
      const label = btn.querySelector('.vb-sidebar__atc-label');
      if (label) {
        if (!btn.dataset.vbAtcLabel) btn.dataset.vbAtcLabel = label.textContent;
        label.textContent = outOfStock ? 'Out of Stock' : btn.dataset.vbAtcLabel;
      }
    });
    updateStepCompletion();
  }

  /** Update step completion indicators */
  function updateStepCompletion() {
    var benchtopComplete = hasBenchtopStep ? !!state.benchtopName : true;
    var steps = {
      1: !!(state.size && (state.bowl || sizesOnly)),
      2: !!state.finish,
      3: benchtopComplete,
      4: state.skipHardware ? true : (hasHardwareStep ? !!(state.hardware && (state.hardwareType === 'push-to-open' || state.hardwareType === 'finger-pull' || state.handleFinish)) : true),
      5: hasBasinStep ? (!!state.basinTouched || state.skipBasin) : true,
      6: state.skipTaphole ? true : !!state.tapholeTouched,
      7: !!state.pwTouched,
    };
    for (var num in steps) {
      var stepEl = builder.querySelector('[data-step="' + num + '"]');
      if (stepEl) {
        stepEl.classList.toggle('vb-step--complete', steps[num]);
      }
    }

    // Mark the Hinge step complete by data-conditional-step (independent of
    // the numeric-step renumbering above — the hinge step's numeric position
    // varies based on which other conditional steps are present).
    if (hasHingeStep) {
      var hingeStepEl = builder.querySelector('[data-conditional-step="hinge"]');
      if (hingeStepEl) hingeStepEl.classList.toggle('vb-step--complete', !!state.hinge);
    }

    // Drawer Side step completion - resolved value (picked or derived)
    // means complete, same by-conditional-step approach as hinge.
    if (hasDrawerFeature) {
      var drawerStepEl = builder.querySelector('[data-conditional-step="drawer"]');
      if (drawerStepEl) drawerStepEl.classList.toggle('vb-step--complete', !!resolveDrawerSide());
    }

    // Fixed-choice taphole completion - the numeric steps{} map above keys
    // on canonical step numbers, but the renumbering pass makes data-step
    // sequential, so on vanities that skip benchtop/hardware/basin the
    // taphole step inherits an always-true slot. Override by conditional
    // step name, same as hinge/drawer. Basin-driven vanities keep their
    // canonical numbering and are untouched.
    if (hasFixedTapholeChoice) {
      var ftStepEl = builder.querySelector('[data-conditional-step="taphole"]');
      if (ftStepEl) ftStepEl.classList.toggle('vb-step--complete', !!state.taphole);
    }

    if (hasHingingFeature) {
      var hingingStepEl = builder.querySelector('[data-conditional-step="hinging"]');
      if (hingingStepEl) hingingStepEl.classList.toggle('vb-step--complete', !!resolveHinging());
    }

    // Update progress bar — count required steps dynamically.
    // The sidebar publishes the total via data-vb-required-total (set from
    // Liquid based on which required rows actually render for this vanity).
    // To guarantee the "done" count stays aligned with that total, we
    // scan the rendered sidebar rows with data-required="true" and count
    // those whose value span has been populated. Earlier bug: the JS
    // treated Size+Bowl as one slot (steps[1]) while Liquid rendered
    // them as two required rows — producing "4 of 5" for multi-size
    // vanities when everything was actually complete.
    var sidebarEl = document.querySelector('.vb-sidebar[data-vb-required-total]');
    var requiredTotal = sidebarEl ? parseInt(sidebarEl.getAttribute('data-vb-required-total'), 10) || 4 : 4;
    var requiredDone = 0;
    if (sidebarEl) {
      sidebarEl.querySelectorAll('.vb-sidebar__item[data-required="true"]').forEach(function (row) {
        var valueEl = row.querySelector('.vb-sidebar__item-value');
        if (valueEl && valueEl.textContent.trim()) requiredDone++;
      });
    } else {
      // Fallback if sidebar not found (shouldn't happen) — use old logic
      requiredDone = (steps[1] ? 1 : 0) + (steps[2] ? 1 : 0) + (steps[3] ? 1 : 0) + (steps[4] ? 1 : 0);
      if (requiredDone > requiredTotal) requiredDone = requiredTotal;
    }
    var pct = Math.round((requiredDone / requiredTotal) * 100);
    var isComplete = requiredDone === requiredTotal;

    var progressFill = $id('vb-progress-fill');
    if (progressFill) {
      // Force width via setProperty with priority=important to win the cascade
      // in case some other rule (theme-css.css / Bootstrap) is setting width.
      progressFill.style.setProperty('width', pct + '%', 'important');
      progressFill.dataset.pct = String(pct);
      progressFill.classList.toggle('vb-sidebar__progress-fill--complete', isComplete);
    }
    var progressLabel = $id('vb-progress-label');
    if (progressLabel) {
      progressLabel.textContent = isComplete
        ? 'All required steps complete'
        : requiredDone + ' of ' + requiredTotal + ' required steps';
    }

    // Sticky bar — show the next step the user should focus on, not a count.
    // Avoids telegraphing "done" (which discourages reviewing optional extras).
    var stepNames = {
      1: 'Size & Config',
      2: 'Cabinet Finish',
      3: 'Benchtop',
      4: 'Handle',
      5: 'Basin',
      6: 'Taphole',
      7: 'Plug & Waste',
    };
    var requiredOrder = [1, 2, 3, 4];
    var optionalOrder = [5, 6, 7];
    var currentStepNum = null;
    for (var i = 0; i < requiredOrder.length; i++) {
      if (!steps[requiredOrder[i]]) { currentStepNum = requiredOrder[i]; break; }
    }
    if (!currentStepNum) {
      for (var j = 0; j < optionalOrder.length; j++) {
        if (!steps[optionalOrder[j]]) { currentStepNum = optionalOrder[j]; break; }
      }
    }
    var stickyName = $id('vb-sticky-step-name');
    var stickyPrefix = $id('vb-sticky-step-prefix');
    if (currentStepNum && stepNames[currentStepNum]) {
      if (stickyName) stickyName.textContent = stepNames[currentStepNum];
      if (stickyPrefix) {
        var isOptional = optionalOrder.indexOf(currentStepNum) !== -1;
        stickyPrefix.innerHTML = 'Step ' + currentStepNum +
          (isOptional ? ' <span class="vb-sticky-bar__optional">(Optional)</span>' : '');
      }
    } else {
      if (stickyName) stickyName.textContent = 'Review & Add';
      if (stickyPrefix) stickyPrefix.textContent = 'Ready';
    }

    // First interaction activates sticky bar visibility (only flips on, never off)
    // and kicks off the smart idle-time preload for later-step popover images.
    if (requiredDone > 0) {
      activateStickyBar();
      schedulePreloadAfterIdle();
    }
  }

  // ── Smart asset preload (Tier 2) ───────────────────────────────
  // After the first meaningful selection, preload the finish images that'll
  // appear in later-step popovers (handle finishes + basin finishes). Uses
  // requestIdleCallback so it never competes with active UI work, and
  // <link rel="prefetch"> so the browser handles priority + caching natively.
  // Fires once per page load.
  var _preloadTriggered = false;
  function schedulePreloadAfterIdle() {
    if (_preloadTriggered) return;
    _preloadTriggered = true;
    var runner = function () { preloadStepAssets(); };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(runner, { timeout: 4000 });
    } else {
      setTimeout(runner, 1500);
    }
  }
  function preloadStepAssets() {
    var filesBase = builder.dataset.filesBase || '';
    var seen = {};
    var urls = [];
    function push(url) {
      if (!url || seen[url]) return;
      seen[url] = true;
      urls.push(url);
    }
    // Handle finishes — one JSON blob per hardware card
    $qa('.vb-card--hardware[data-handle-finishes]').forEach(function (card) {
      try {
        var finishes = JSON.parse(card.dataset.handleFinishes);
        (finishes || []).forEach(function (f) {
          if (f && f.img) push(f.img.indexOf('http') === 0 ? f.img : filesBase + f.img);
        });
      } catch (e) { /* ignore parse errors */ }
    });
    // Basin finishes — from the already-parsed global finishesData object
    if (finishesData && typeof finishesData === 'object') {
      for (var key in finishesData) {
        var entry = finishesData[key];
        if (entry && entry.finishes) {
          entry.finishes.forEach(function (f) {
            if (f && f.img) push(f.img.indexOf('http') === 0 ? f.img : filesBase + f.img);
          });
        }
      }
    }
    if (!urls.length) return;
    // Create prefetch links in one fragment to avoid repeated DOM reflows.
    var frag = document.createDocumentFragment();
    urls.forEach(function (url) {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = url;
      frag.appendChild(link);
    });
    document.head.appendChild(frag);
  }

  // ── Mobile sticky total bar ────────────────────────────────────
  var _stickyActivated = false;
  function activateStickyBar() {
    if (_stickyActivated) return;
    _stickyActivated = true;
    evaluateStickyBarVisibility();
  }
  function evaluateStickyBarVisibility() {
    var bar = $id('vb-sticky-bar');
    if (!bar || !_stickyActivated) return;
    // Hide when the full mobile summary is already in view, or when the whole
    // builder has scrolled out of view. Otherwise, show.
    var shouldShow = _builderInView && !_mobileSummaryInView;
    bar.classList.toggle('vb-sticky-bar--visible', shouldShow);
    bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    bar.setAttribute('tabindex', shouldShow ? '0' : '-1');
  }
  var _builderInView = true;
  var _mobileSummaryInView = false;
  function initStickyBar() {
    var bar = $id('vb-sticky-bar');
    if (!bar) return;
    var summary = document.querySelector('.vb-mobile-summary');
    var root = $id('vanity-builder');

    bar.addEventListener('click', function () {
      if (summary) summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    if ('IntersectionObserver' in window) {
      if (summary) {
        new IntersectionObserver(function (entries) {
          _mobileSummaryInView = entries[0].isIntersecting;
          evaluateStickyBarVisibility();
        }, { threshold: 0.15 }).observe(summary);
      }
      if (root) {
        new IntersectionObserver(function (entries) {
          _builderInView = entries[0].isIntersecting;
          evaluateStickyBarVisibility();
        }, { threshold: 0 }).observe(root);
      }
    }
  }

  // ── Matrix helpers ─────────────────────────────────────────────

  /** Get the SKU code for the current size + bowl selection */
  /** Normalise bowl name to match matrix keys (e.g. "Left Offset Bowl" -> "Left Bowl") */
  function normaliseBowl(bowl) {
    if (!bowl) return bowl;
    return bowl.replace(' Offset ', ' ');
  }

  /** Get the base SKU for the current size + bowl (+ top type or cabinet type if applicable) */
  function getConfigSku() {
    if (!matrixData || !state.size || !state.bowl) return null;
    var bowl = normaliseBowl(state.bowl);
    if (state.cabinetType) {
      // 3-part key for vanities with cabinet types (e.g. "1350|Centre Bowl|All Door")
      var key3 = state.size + '|' + bowl + '|' + state.cabinetType;
      return matrixData.config_map[key3] || null;
    }
    var key2 = state.size + '|' + bowl;
    if (hasTopTypes && state.topType) {
      // 3-part key for vanities with top types (e.g. "1200|Centre Bowl|Cherry Pie").
      // Fall back to the 2-part key for vanities whose matrix predates the
      // per-top-type schema (Thea declares topTypes for Matte Ceramic but its
      // matrix only carries the base CP key). Cherry Pie / Durasein /
      // Caesarstone share the same basin-compat row, so the base SKU is the
      // correct fallback. Moulded tops bypass this via _mouldedTapholeCode
      // before getConfigSku() is reached.
      var key3 = state.size + '|' + bowl + '|' + state.topType;
      return matrixData.config_map[key3] || matrixData.config_map[key2] || null;
    }
    return matrixData.config_map[key2] || null;
  }

  /** Map benchtop material to SKU suffix */
  var BENCHTOP_SKU_SUFFIX = VB_CONFIG.benchtopSkuSuffixes || {
    'Cherry Pie Premium Solid Surface': 'CP',
    'Durasein': 'DU',
  };

  /** Get the full SKU adjusted for benchtop selection */
  function getFullSku() {
    var baseSku = getConfigSku();
    if (!baseSku) return null;
    if (!state.benchtopMaterial) return baseSku;
    var suffix = BENCHTOP_SKU_SUFFIX[state.benchtopMaterial];
    if (!suffix || baseSku.endsWith(suffix)) return baseSku;
    // Replace last 2 characters with the correct suffix
    return baseSku.slice(0, -2) + suffix;
  }

  /** Update the SKU display in the sidebar */
  function updateSkuDisplay() {
    var sku = getFullSku();
    updateSummary('sku', sku || '');
  }

  /**
   * Filter basin cards based on the current vanity configuration.
   * Hides basins marked 'X' in the matrix. Shows compatible ones.
   */
  function filterBasinsByConfig() {
    if (!matrixData) return;

    var sku = getConfigSku();
    if (!sku) return;

    var configMatrix = matrixData.matrix[sku];
    if (!configMatrix) return;

    // Get all basin cards (including "No Basin")
    var allBasinCards = $qa('[data-option-group="basin"] .vb-card--basin');

    var toShow = [];
    allBasinCards.forEach(function (card) {
      var basinKey = card.dataset.basinKey;
      if (!basinKey) return; // "No Basin" — always visible (has no basin-key)

      var code = configMatrix[basinKey] || 'X';

      if (code === 'X') {
        card.classList.add('d-none');
      } else {
        card.classList.add('d-none'); // hide for staggered entrance
        toShow.push(card);
      }
    });

    // Staggered entrance animation for visible basin cards
    animateCardsIn(toShow);

    // Hide basin type labels if all basins in that type are hidden
    $qa('.vb-step__label').forEach(function (label) {
      var next = label.nextElementSibling;
      if (!next || !next.matches('[data-option-group="basin"]')) return;

      var visibleCards = next.querySelectorAll('.vb-card--basin:not(.d-none)');
      label.classList.toggle('d-none', visibleCards.length === 0);
      next.classList.toggle('d-none', visibleCards.length === 0);
    });

    // Reset basin selection if currently selected basin is now hidden
    if (state.basinHandle) {
      var selectedCard = $q('.vb-card--basin[data-basin-handle="' + state.basinHandle + '"]');
      if (selectedCard && selectedCard.classList.contains('d-none')) {
        var noBasin = $q('.vb-card--basin[data-basin-handle=""]');
        if (noBasin) noBasin.click();
      }
    }
  }

  /**
   * Update taphole options based on the selected basin + vanity config.
   * Shows only valid taphole positions from the matrix code.
   */
  function updateTapholeOptions() {
    updateTapholeVisibility();

    if (!matrixData) return;

    var sku = getConfigSku();
    if (!sku) return;

    // For moulded tops (Ceramic/Imperial), use the configured taphole code
    if (state._mouldedTapholeCode !== undefined && state.skipBasin) {
      var code = state._mouldedTapholeCode;
      filterTapholesByCode(code);
      return;
    }

    var configMatrix = matrixData.matrix[sku];
    if (!configMatrix) return;

    // If no basin selected, hide taphole options
    if (!state.basinKey) {
      return;
    }

    var code = configMatrix[state.basinKey];

    if (!code || code === 'X') {
      showAllTapholeCards();
      return;
    }

    if (state.isDouble) {
      // Double bowl — generate paired cards
      var container = $q('[data-option-group="taphole-pairs"]');
      if (container) {
        container.innerHTML = '';
        var pairs = generateTapholePairs(code);
        renderTapholePairCards(container, pairs);
        // Auto-select if only one real option
        if (pairs.length === 1 || (pairs.length > 0 && pairs[0].autoSelect)) {
          var firstCard = container.querySelector('.vb-card--taphole-pair');
          if (firstCard) firstCard.click();
        }
      }
    } else {
      // Single/offset bowl — use existing card filtering
      // Handle special codes
      if (code === '*') {
        filterTapholeGroup('taphole', null, true);
        return;
      }
      var positions = TAPHOLE_CODES[code] || [];
      filterTapholeGroup('taphole', positions, false);
    }
  }

  /** Filter taphole options using a direct code (for moulded tops without basin selection) */
  function filterTapholesByCode(code) {
    if (code === '0') {
      // No taphole — auto-select "No Taphole"
      filterTapholeGroup('taphole', [], false);
      var noTapholeCard = $q('[data-option-group="taphole"] .vb-card--taphole[data-taphole="No Taphole"]');
      if (noTapholeCard && !noTapholeCard.classList.contains('vb-card--active')) {
        noTapholeCard.click();
      }
    } else if (code === '*') {
      filterTapholeGroup('taphole', null, true);
    } else if (state.isDouble) {
      // Double bowl — generate paired cards same as regular flow
      var container = $q('[data-option-group="taphole-pairs"]');
      if (container) {
        container.innerHTML = '';
        var pairs = generateTapholePairs(code);
        renderTapholePairCards(container, pairs);
        if (pairs.length === 1 || (pairs.length > 0 && pairs[0].autoSelect)) {
          var firstCard = container.querySelector('.vb-card--taphole-pair');
          if (firstCard) firstCard.click();
        }
      }
    } else {
      var positions = TAPHOLE_CODES[code] || [];
      filterTapholeGroup('taphole', positions, false);
    }
  }

  /**
   * Filter taphole cards within a group to show only allowed positions.
   * @param {string} groupName - "taphole", "taphole-left", or "taphole-right"
   * @param {string[]|null} positions - allowed clock positions (e.g. ["10", "12", "2"]), null for in-basin
   * @param {boolean} inBasin - if true, only show "Taphole in Basin" card
   */
  function filterTapholeGroup(groupName, positions, inBasin) {
    var group = $q('[data-option-group="' + groupName + '"]');
    if (!group) return;

    var cards = group.querySelectorAll('.vb-card--taphole');
    var currentSelection = null;
    var toAnimate = [];

    // First hide all, then determine which should be visible
    cards.forEach(function (card) { card.classList.add('d-none'); });

    cards.forEach(function (card) {
      var tapholeVal = card.dataset.taphole;

      if (inBasin) {
        if (tapholeVal === 'Taphole in Basin') {
          toAnimate.push(card);
          card.classList.remove('d-none');
          card.click(); // Auto-select
        }
        return;
      }

      if (tapholeVal === 'No Taphole') {
        card.classList.remove('d-none');
        // "No Taphole" is always visible, no entrance animation
        return;
      }

      if (tapholeVal === 'Taphole in Basin') {
        return; // stays hidden
      }

      if (!positions || positions.length === 0) {
        return; // stays hidden
      }

      var clockNum = tapholeVal.replace(" O'Clock", '');
      if (positions.indexOf(clockNum) !== -1) {
        toAnimate.push(card);
      }
    });

    // Staggered entrance for newly visible taphole cards
    animateCardsIn(toAnimate);

    // If code is '0', auto-select "No Taphole"
    if (!inBasin && (!positions || positions.length === 0)) {
      var noTaphole = group.querySelector('.vb-card--taphole[data-taphole="No Taphole"]');
      if (noTaphole) noTaphole.click();
    }

    // Reset selection if currently active card is now hidden
    var activeCard = group.querySelector('.vb-card--active.d-none');
    if (activeCard) {
      activeCard.classList.remove('vb-card--active');
      // Reset the corresponding state
      if (groupName === 'taphole') state.taphole = null;
      else if (groupName === 'taphole-left') state.tapholeLeft = null;
      else if (groupName === 'taphole-right') state.tapholeRight = null;
      updateSummary('taphole', '--');
    }
  }

  /** Show or hide the entire taphole step content based on basin selection (or fixed taphole config) */
  function updateTapholeVisibility() {
    var hasBowl = !!(state.bowl);
    var hasBasin = !!(state.basinKey);
    var hasMouldedTop = !!(state.skipBasin && state._mouldedTapholeCode !== undefined);
    var ready = hasBowl && (hasBasin || hasMouldedTop || hasFixedTaphole);
    var placeholder = $q('.vb-taphole-placeholder');
    var singleWrap = $q('.vb-taphole-single');
    var doubleWrap = $q('.vb-taphole-double');

    if (placeholder) {
      placeholder.classList.toggle('d-none', ready);
      if (!ready) {
        var missing = [];
        if (!hasBowl) missing.push('<a href="#" class="vb-taphole-placeholder__link" data-scroll-to="bowl">Bowl Position</a>');
        if (!hasBasin && !hasFixedTaphole) missing.push('<a href="#" class="vb-taphole-placeholder__link" data-scroll-to="basin">Basin</a>');
        placeholder.innerHTML = 'Select a ' + missing.join(' and ') + ' to see available taphole positions';
      }
    }
    if (ready) {
      // Imperial Top etc. lock the taphole choice and show an explanatory
      // note — hide the cards so they don't compete with the note.
      if (state.tapholeLocked) {
        if (singleWrap) singleWrap.classList.add('d-none');
        if (doubleWrap) doubleWrap.classList.add('d-none');
      } else {
        // Fixed-taphole vanities (Zurich family) drill the whole top to one
        // spec, so doubles make the same single choice - never the per-basin
        // pairs UI (its cards are basin-styled and outside the allowlist).
        if (singleWrap) singleWrap.classList.toggle('d-none', state.isDouble && !hasFixedTaphole);
        if (doubleWrap) doubleWrap.classList.toggle('d-none', !state.isDouble || hasFixedTaphole);
      }

      // Clear auto "No Taphole" if user hasn't intentionally selected a taphole
      if (!state.tapholeTouched) {
        updateSummary('taphole', null);
        updateDoubleTapholePairSummary(null, null);
      }
    } else {
      // Hide both layouts
      if (singleWrap) singleWrap.classList.add('d-none');
      if (doubleWrap) doubleWrap.classList.add('d-none');

      // Auto "No Taphole" in sidebar when no basin is selected
      if (!hasBasin && state.basinTouched) {
        updateSummary('taphole', 'No Taphole');
        updateDoubleTapholePairSummary('No Taphole', 'No Taphole');
      }
    }
  }

  // Click handler for taphole placeholder links — scroll to the missing field
  var tpPlaceholder = $q('.vb-taphole-placeholder');
  if (tpPlaceholder) {
    tpPlaceholder.addEventListener('click', function (e) {
      var link = e.target.closest('.vb-taphole-placeholder__link');
      if (!link) return;
      e.preventDefault();
      var key = link.dataset.scrollTo;
      var targets = {
        bowl: { scroll: '.vb-bowl-cards', step: '1' },
        basin: { scroll: '[data-option-group="basin"]', step: '5' },
      };
      var mapping = targets[key];
      if (!mapping) return;
      var scrollEl = $q(mapping.scroll);
      var stepEl = scrollEl ? scrollEl.closest('.vb-step') : $q('.vb-step[data-step="' + mapping.step + '"]');
      if (scrollEl && stepEl) {
        scrollThenPulse(scrollEl, stepEl, 'vb-step--pulse');
      }
    });
  }

  /** Show all taphole cards (reset filtering) */
  function showAllTapholeCards() {
    $qa('.vb-card--taphole').forEach(function (card) {
      // Hide "Taphole in Basin" by default, show everything else
      if (card.dataset.taphole === 'Taphole in Basin') {
        card.classList.add('d-none');
      } else {
        card.classList.remove('d-none');
      }
    });
  }

  /** For vanities with fixed taphole options (no basin step), filter cards to only show configured options */
  function applyFixedTapholeFilter() {
    if (!hasFixedTaphole) return;
    var allowed = VB_CONFIG.fixedTapholeOptions;
    $qa('.vb-card--taphole').forEach(function (card) {
      if (allowed.indexOf(card.dataset.taphole) >= 0) {
        card.classList.remove('d-none');
      } else {
        card.classList.add('d-none');
      }
    });
  }

  // Apply fixed taphole filter on init if applicable
  if (hasFixedTaphole) applyFixedTapholeFilter();

  // ── Step 1: Size & Bowl ────────────────────────────────────────
  const sizeCards = $qa('.vb-card--size');
  const bowlCards = $qa('.vb-card--bowl');
  const bowlContainer = $q('.vb-bowl-cards');

  // Single-size vanities (small-space): the Liquid hides the Size subsection
  // when only one size exists, so there's no size click to reveal bowl cards.
  // Derive the single size from any bowl card's dataset and auto-set state.size
  // so bowl-card selection + downstream logic (cabinet finish, P&W, etc.) works.
  if (sizeCards.length === 0 && bowlCards.length > 0) {
    var firstBowl = bowlCards[0];
    state.size = firstBowl.dataset.size || '';
    // Reveal all bowl cards so the customer can pick between them.
    bowlCards.forEach(function (bc) { bc.classList.remove('d-none'); });
    animateCardsIn(Array.from(bowlCards));
  }

  /** Restore size cards on a size click.
   *  - Active class (tick) is cleared on EVERY size card — the tick only
   *    appears after a configuration is committed in the overlay.
   *  - Image + sub-label is preserved on the just-clicked card (`skipCard`)
   *    so re-opening the overlay for a previously-configured size keeps
   *    that card's render visible until a new pick replaces it. */
  function restoreSizeCardImages(skipCard) {
    sizeCards.forEach(function (sc) {
      sc.classList.remove('vb-card--active');
      if (skipCard && sc === skipCard) return;
      var img = sc.querySelector('.vb-card__img');
      if (img && sc.dataset.originalImgSrc) {
        img.src = sc.dataset.originalImgSrc;
      }
      var label = sc.querySelector('.vb-card__label');
      if (label) {
        var sublabel = label.querySelector('.vb-card__finish-label');
        if (sublabel) sublabel.remove();
      }
    });
  }

  /** Reflect the chosen bowl/configuration on the active size card.
   *  Copies the bowl card's image into the size card and appends a
   *  bowl-name sub-label, mirroring the basin/handle pattern.
   *  Label text matches the sidebar bowl summary so integrated-top + cabinet-only
   *  cards read sensibly (e.g. "Matte Ceramic Single Bowl", "Cabinet Only"). */
  function reflectBowlOnSizeCard(bowlCard) {
    var sizeCard = Array.from(sizeCards).find(function (sc) {
      return sc.dataset.size === state.size
          && (sc.dataset.cabinetType || '') === (state.cabinetType || '');
    });
    if (!sizeCard) return;

    var img = sizeCard.querySelector('.vb-card__img');
    if (img) {
      if (!sizeCard.dataset.originalImgSrc) {
        sizeCard.dataset.originalImgSrc = img.src;
      }
      var bowlImg = bowlCard.querySelector('.vb-card__img');
      if (bowlImg) img.src = bowlImg.src;
    }

    var labelText = bowlCard.dataset.bowl || '';
    if (bowlCard.dataset.integratedTop === 'true') {
      var topName = (bowlCard.dataset.topVariant || '').replace(/\s+Top$/, '');
      labelText = topName + ' ' + (state.isDouble ? 'Double' : 'Single') + ' Bowl';
    }

    var label = sizeCard.querySelector('.vb-card__label');
    if (label) {
      var sublabel = label.querySelector('.vb-card__finish-label');
      if (!sublabel) {
        sublabel = document.createElement('span');
        sublabel.className = 'vb-card__finish-label';
        label.appendChild(sublabel);
      }
      sublabel.textContent = labelText;
    }

    // Mark this size card as the active selection (tick visible). Held off
    // until bowl pick so a multi-bowl size doesn't show a tick while the
    // overlay is still open and uncommitted.
    setActive(sizeCards, sizeCard);
  }

  sizeCards.forEach((card) => {
    card.addEventListener('click', () => {
      // Glacier owns its own size-card click flow (vanity-builder-glacier.js
      // openGlacierConfigOverlay) — exit early so this handler doesn't
      // also fire and call openConfigOverlay() with stale state, which
      // creates a competing overlay whose close button throws because
      // bowlContainer (.vb-bowl-cards) doesn't exist on Glacier.
      if (builder.dataset.productHandle === 'glacier-full-depth') return;

      // Sizes-only products: the size card carries its Standard variant, so
      // a click commits the whole Step 1 selection (express-card pattern)
      // and no Configuration overlay exists. The express-exit listener
      // (registered later) handles leaving Express mode and knows not to
      // drop the variant this handler just committed.
      if (sizesOnly) {
        state.size = card.dataset.size;
        state.cabinetType = null;
        state.bowl = null;
        state.isDouble = false;
        state.variantId = card.dataset.variantId;
        state.variantPrice = parseInt(card.dataset.variantPrice, 10);
        state.variantComparePrice = parseInt(card.dataset.variantComparePrice, 10) || 0;
        state.variantAvailable = card.dataset.variantAvailable !== 'false';
        setActive(sizeCards, card);
        var soImg = card.querySelector('img');
        updateSidebarThumb('size', soImg ? soImg.src : '');
        updateSummary('size', state.size + 'mm');
        updateSkuDisplay();
        updatePrice();
        updateATCState();
        return;
      }

      var isSameSize = state.size === card.dataset.size
        && (state.cabinetType || null) === (card.dataset.cabinetType || null);

      // Re-clicking the same size is just "reopen the overlay" — preserve
      // any existing bowl commitment (state.bowl, variantId, tick on the
      // size card) so closing via the X button leaves the prior selection
      // intact. Only a *different* size click resets the commitment and
      // clears the active tick.
      if (!isSameSize) {
        state.size = card.dataset.size;
        state.cabinetType = card.dataset.cabinetType || null;
        state.bowl = null;
        state.variantId = null;

        // Clear active class on ALL size cards (only re-applied after a bowl
        // is picked from the overlay). Image + sub-label preserved on the
        // just-clicked card so re-opening overlay later keeps the render.
        restoreSizeCardImages(card);

        var sizeImg = card.querySelector('img');
        updateSidebarThumb('size', sizeImg ? sizeImg.src : '');

        updateSummary('size', state.size + 'mm');
        updateSummary('bowl', null);
        updateSidebarThumb('bowl', '');
        updateSkuDisplay();
        updatePrice();
        updateATCState();
      }

      // Open the Configuration overlay. It handles card sectioning,
      // positioning, and animation. If only one option applies for the
      // selected size, the overlay auto-selects it and closes.
      openConfigOverlay(card);
    });
  });

  // ── Step 1 Configuration overlay ────────────────────────────────
  /** Open the Configuration overlay anchored to the clicked size card.
   *  Filters bowl cards by current size+cabinetType, groups them into
   *  three sections (Cabinet Only / Bowl Position / Integrated Top),
   *  and presents them in a popover styled after the basin-finish
   *  overlay. Close happens via card click, close button, or
   *  click-outside. */
  function openConfigOverlay(sizeCard) {
    closeConfigOverlay();

    var bowlOrder = { 'Left': 1, 'Centre': 2, 'Center': 2, 'Right': 3, 'Double': 4 };
    var visible = Array.from(bowlCards).filter(function (bc) {
      var sizeMatch = bc.dataset.size === state.size;
      var cabMatch = !state.cabinetType || (bc.dataset.cabinetType || '') === state.cabinetType;
      return sizeMatch && cabMatch;
    });

    // Partition into the three sections by card data attrs.
    var cabinetOnly = [];
    var bowlPosition = [];
    var integratedTop = [];
    visible.forEach(function (c) {
      if (c.dataset.cabinetOnly === 'true') cabinetOnly.push(c);
      else if (c.dataset.integratedTop === 'true') integratedTop.push(c);
      else bowlPosition.push(c);
    });
    bowlPosition.sort(function (a, b) {
      var aKey = Object.keys(bowlOrder).find(function (k) { return a.dataset.bowl.includes(k); }) || '';
      var bKey = Object.keys(bowlOrder).find(function (k) { return b.dataset.bowl.includes(k); }) || '';
      return (bowlOrder[aKey] || 99) - (bowlOrder[bKey] || 99);
    });
    // Integrated top: Matte Ceramic before Imperial Top.
    var itOrder = { 'Matte Ceramic': 0, 'Imperial Top': 1 };
    integratedTop.sort(function (a, b) {
      return (itOrder[a.dataset.topVariant] || 99) - (itOrder[b.dataset.topVariant] || 99);
    });

    if (visible.length === 0) return;

    // If only one card is applicable for this size — skip the overlay,
    // auto-select it, and return. Matches the previous inline behaviour.
    // The card must be re-hidden after the click: the bowl-click handler
    // calls closeConfigOverlay which only restores cards that were inside
    // the popover, so without this the card would linger visible below
    // the size grid.
    if (visible.length === 1) {
      visible[0].classList.remove('d-none');
      visible[0].click();
      visible[0].classList.add('d-none');
      return;
    }

    var popover = document.createElement('div');
    popover.className = 'vb-config-overlay';
    popover.id = 'vb-config-overlay';

    var header = document.createElement('div');
    header.className = 'vb-config-overlay__header';
    header.innerHTML =
      '<h3 class="vb-config-overlay__title">' + state.size + 'mm — Select Configuration</h3>' +
      '<button type="button" class="vb-config-overlay__close" aria-label="Close">' +
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>';
    header.querySelector('.vb-config-overlay__close').addEventListener('click', function (e) {
      e.stopPropagation();
      closeConfigOverlay();
    });
    popover.appendChild(header);

    function appendSection(title, cards) {
      if (!cards.length) return;
      var section = document.createElement('div');
      section.className = 'vb-config-overlay__section';
      var h = document.createElement('div');
      h.className = 'vb-config-overlay__section-title';
      h.textContent = title;
      section.appendChild(h);
      var grid = document.createElement('div');
      grid.className = 'vb-config-overlay__grid';
      cards.forEach(function (c) {
        c.classList.remove('d-none');
        grid.appendChild(c);
      });
      section.appendChild(grid);
      popover.appendChild(section);
    }

    appendSection('Cabinet Only', cabinetOnly);
    appendSection('Bowl Position', bowlPosition);
    appendSection('Integrated Top', integratedTop);

    // Anchor the overlay below the size card, positioned within the
    // size card's grid row so it expands the natural flow.
    var sizeGrid = sizeCard.closest('.vb-cards');
    if (sizeGrid) sizeGrid.style.position = 'relative';
    var host = sizeGrid || sizeCard.parentElement;
    host.appendChild(popover);

    var cardOffsetTop = sizeCard.offsetTop + sizeCard.offsetHeight + 8;
    popover.style.top = cardOffsetTop + 'px';

    animateCardsIn(Array.from(popover.querySelectorAll('.vb-card--bowl')));

    setTimeout(function () {
      document.addEventListener('click', handleConfigOverlayOutsideClick);
    }, 100);

    // Smooth-scroll so the overlay is visible without jarring jumps.
    setTimeout(function () {
      var headerEl = document.querySelector('.section-header, .shopify-section-header, header');
      var headerHeight = headerEl ? headerEl.offsetHeight : 0;
      var scrollY = sizeCard.getBoundingClientRect().top + window.pageYOffset - headerHeight - 40;
      window.scrollTo({ top: scrollY, behavior: 'smooth' });
    }, 80);
  }

  function closeConfigOverlay() {
    var popover = $id('vb-config-overlay');
    if (!popover) return;
    // Return bowl cards to the main .vb-bowl-cards container and hide
    // them again so they're ready for the next overlay open.
    popover.querySelectorAll('.vb-card--bowl').forEach(function (c) {
      c.classList.add('d-none');
      bowlContainer.appendChild(c);
    });
    document.removeEventListener('click', handleConfigOverlayOutsideClick);
    popover.remove();
  }

  function handleConfigOverlayOutsideClick(e) {
    var popover = $id('vb-config-overlay');
    if (!popover) return;
    if (popover.contains(e.target)) return;
    if (e.target.closest('.vb-card--size')) return; // let size re-click reopen
    closeConfigOverlay();
  }

  bowlCards.forEach((card) => {
    card.addEventListener('click', () => {
      // Glacier: same as the size-card guard above. Glacier's bowl cards
      // share the .vb-card--bowl class but are owned by glacier JS
      // (handleBowlPickClick). Bail before existing variant resolution
      // logic runs and clobbers Glacier's state.
      if (builder.dataset.productHandle === 'glacier-full-depth') return;

      const visible = Array.from(bowlCards).filter((bc) => bc.dataset.size === state.size);
      setActive(visible, card);

      state.bowl = card.dataset.bowl;
      var isCabinetOnlyCard = card.dataset.cabinetOnly === 'true';
      var isIntegratedTopCard = card.dataset.integratedTop === 'true';

      if (isCabinetOnlyCard) {
        // Cabinet Only is its own mode — flip all downstream skip flags
        // and resolve the variant directly from the card's data attrs
        // (no topType resolution needed since Cabinet Only IS the variant).
        enterCabinetOnlyMode();
        state.variantId = card.dataset.variantId;
        state.variantPrice = parseInt(card.dataset.variantPrice, 10);
        state.variantComparePrice = parseInt(card.dataset.variantComparePrice, 10) || 0;
        state.variantAvailable = card.dataset.variantAvailable !== 'false';
      } else if (isIntegratedTopCard) {
        // Matte Ceramic / Imperial Top — integrated top+basin. Skip basin
        // only; Handle + Taphole + P&W stay in play.
        enterIntegratedTopMode(card.dataset.topVariant);
        state.variantId = card.dataset.variantId;
        state.variantPrice = parseInt(card.dataset.variantPrice, 10);
        state.variantComparePrice = parseInt(card.dataset.variantComparePrice, 10) || 0;
        state.variantAvailable = card.dataset.variantAvailable !== 'false';
      } else {
        // Regular bowl — undo any prior Cabinet-Only or Integrated-Top UI
        // state so the customer can switch freely between them.
        resetCabinetOnlyMode();
        resetIntegratedTopMode();

        if (hasTopTypes || hasTopVariants) {
          // Default to Cherry Pie (Phase C baseline — every benchtop vanity has it).
          // An already-selected non-special topType survives; if we were in
          // Cabinet Only or Integrated Top mode, topType still holds that
          // old value — force back to Cherry Pie.
          if (!state.topType
              || state.topType === 'Cabinet Only'
              || state.topType === 'Matte Ceramic'
              || state.topType === 'Imperial Top') {
            state.topType = 'Cherry Pie';
          }
          resolveVariant();
          if (hasTopTypes) updateTopTypePrices();
        } else {
          state.variantId = card.dataset.variantId;
          state.variantPrice = parseInt(card.dataset.variantPrice, 10);
          state.variantComparePrice = parseInt(card.dataset.variantComparePrice, 10) || 0;
          state.variantAvailable = card.dataset.variantAvailable !== 'false';
        }
      }

      // Drift-top cards (No Taphole / 1 Taphole, illustrated as the
      // integrated solid-surface top) mirror horizontally when a Right Bowl
      // variant is chosen so the waste hole + taphole dot land on the
      // physically correct side.
      var isRightBowl = !!(state.bowl && state.bowl.toLowerCase().indexOf('right') >= 0);
      $qa('.vb-card--taphole-drift-top').forEach(function (c) {
        c.classList.toggle('vb-card--drift-mirrored', isRightBowl);
      });

      var bowlImg = card.querySelector('img');
      updateSidebarThumb('bowl', bowlImg ? bowlImg.src : '');

      // Toggle single/double taphole layout
      state.isDouble = !!(state.bowl && state.bowl.toLowerCase().includes('double'));
      updateTapholeVisibility();

      // Reflect the chosen bowl on the active size card (image swap + sub-label).
      // Runs after state.isDouble so integrated-top label reads correctly.
      reflectBowlOnSizeCard(card);

      // Toggle single/double taphole sidebar rows
      $qa('.vb-sidebar__taphole-single').forEach(function (el) { el.classList.toggle('d-none', state.isDouble && !hasFixedTaphole); });
      $qa('.vb-sidebar__taphole-double').forEach(function (el) { el.classList.toggle('d-none', !state.isDouble || hasFixedTaphole); });

      // Reset taphole selections when switching
      state.taphole = null;
      state.tapholeLeft = null;
      state.tapholeRight = null;
      state.tapholeTouched = false;
      $qa('.vb-card--taphole').forEach((c) => c.classList.remove('vb-card--active'));
      // Clear dynamic pair cards
      var pairContainer = $q('[data-option-group="taphole-pairs"]');
      if (pairContainer) pairContainer.innerHTML = '';
      updateSummary('taphole', null);
      updateDoubleTapholePairSummary(null, null);
      updateSidebarThumb('taphole', '');
      updateSidebarThumb('taphole-double', '');
      // Remove any SVG thumbs left from previous selection
      $qa('.vb-sidebar__item-thumb-svg').forEach(function (svg) {
        var parent = svg.closest('[data-summary="taphole"], [data-summary="taphole-double"]');
        if (parent) svg.remove();
      });

      // For integrated-top cards (Imperial Top / Matte Ceramic), the
      // basin position is built into the top, so "Centre Bowl" /
      // "Double Bowl" doesn't carry meaningful information by itself.
      // Show the top variant + Single/Double instead — e.g.
      // "Imperial Single Bowl", "Matte Ceramic Double Bowl".
      var bowlSummary = state.bowl;
      if (isIntegratedTopCard) {
        var topName = (card.dataset.topVariant || '').replace(/\s+Top$/, '');
        bowlSummary = topName + ' ' + (state.isDouble ? 'Double' : 'Single') + ' Bowl';
      }
      updateSummary('bowl', bowlSummary);
      updateSkuDisplay();
      filterBasinsByConfig();
      updateBasinDisplay();
      updatePlugWasteDisplay();
      updateTapholeOptions();
      updatePrice();
      updateATCState();
      // updateSidebarRender(); // Disabled — render images not available for all vanities yet

      // Close the Configuration overlay now that a selection has been
      // made. The overlay is only opened from size-card clicks, so this
      // is safe to call unconditionally — it no-ops if the overlay
      // isn't open.
      closeConfigOverlay();
    });
  });

  // No auto-selection on load — user must pick a size
  updateTapholeVisibility();

  // ── Step 2: Cabinet Finish ─────────────────────────────────────
  const finishSwatches = $qa('[data-option-group="finish"] .vb-swatch');
  finishSwatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      setActive(finishSwatches, swatch);
      state.finish = swatch.dataset.finishName;
      var finishImg = swatch.querySelector('img');
      updateSidebarThumb('finish', finishImg ? finishImg.src : '');
      updateSummary('finish', state.finish);
      updateATCState();
      // updateSidebarRender(); // Disabled — render images not available for all vanities yet
    });
  });

  // ── Express Ship cards (Step 1 second section, Dispatch axis) ──
  // Rendered only for vanities whose variants carry option2 "Express Ship"
  // (Zurich pattern: same builder, stocked sizes in a restricted finish set,
  // next-day dispatch). One click commits size + bowl + variant, since each
  // Express card IS a specific variant. On every other vanity this NodeList
  // is empty and the whole block no-ops.
  const expressCards = $qa('.vb-card--express');
  if (expressCards.length) {
    var expressAllowedFinishes = VB_CONFIG.expressFinishes || [];

    // Hide Step 2 swatches outside the Express finish set while an Express
    // card is active; restore everything on exit. If the currently selected
    // finish is not Express-stocked, clear it so ATC re-locks until a valid
    // swatch is picked (prevents an Express variant reaching the cart with a
    // non-stocked Cabinet Finish property).
    var applyExpressFinishFilter = function (on) {
      if (!expressAllowedFinishes.length) return;
      finishSwatches.forEach(function (swatch) {
        var allowed = expressAllowedFinishes.indexOf(swatch.dataset.finishName) !== -1;
        var item = swatch.closest('.vb-swatch-item') || swatch;
        item.classList.toggle('d-none', on && !allowed);
      });
      var note = $q('.vb-express-finish-note');
      if (note) note.classList.toggle('d-none', !on);
      if (on && state.finish && expressAllowedFinishes.indexOf(state.finish) === -1) {
        state.finish = null;
        finishSwatches.forEach(function (s) { s.classList.remove('vb-swatch--active'); });
        updateSidebarThumb('finish', '');
        updateSummary('finish', null);
      }
    };

    expressCards.forEach(function (card) {
      card.addEventListener('click', function () {
        state.dispatch = 'Express Ship';
        state.size = card.dataset.size;
        state.bowl = sizesOnly ? null : (card.dataset.bowl || 'Centre Bowl');
        state.cabinetType = null;
        state.isDouble = !!state.bowl && state.bowl.indexOf('Double') !== -1;
        state.variantId = card.dataset.variantId;
        state.variantPrice = parseInt(card.dataset.variantPrice, 10);
        state.variantComparePrice = parseInt(card.dataset.variantComparePrice, 10) || 0;
        state.variantAvailable = card.dataset.variantAvailable !== 'false';

        // Commit the whole Step 1 selection in one click: clear standard
        // size-card ticks AND bowl-card ticks (on single-size vanities the
        // bowl cards ARE the visible standard cards - Chris caught a stale
        // tick surviving there), mark this card, mirror into the sidebar.
        restoreSizeCardImages();
        bowlCards.forEach(function (c) { c.classList.remove('vb-card--active'); });
        setActive(expressCards, card);
        var exImg = card.querySelector('img');
        updateSidebarThumb('size', exImg ? exImg.src : '');
        updateSummary('size', state.size + 'mm · Express Ship');
        updateSummary('bowl', state.bowl);
        updateSidebarThumb('bowl', exImg ? exImg.src : '');
        // Express fixed-build locks. Express is a fixed stocked
        // build: 1 taphole top, right-hand drawers (handled generically
        // by resolveDrawerSide), left-hinged door on single-door minis.
        // The steps STAY VISIBLE but lock (vb-step--express-locked): the
        // fixed value shows ticked at full strength, the alternatives dim
        // and go inert - an Express-first customer still sees the choices
        // a Standard build offers (Chris; Durasein thickness-lock pattern).
        // State carries the fixed values, so the ATC gates pass and the
        // line item properties still emit - the order states the build.
        if (hasFixedTapholeChoice) {
          state.taphole = '1 Taphole';
          state.tapholeTouched = true;
          var thStepLock = $q('[data-conditional-step="taphole"]');
          if (thStepLock) thStepLock.classList.add('vb-step--express-locked');
          $qa('.vb-card--taphole').forEach(function (c) {
            c.classList.toggle('vb-card--active', c.dataset.taphole === '1 Taphole');
          });
          updateTapholeVisibility();
          updateSummary('taphole', '1 Taphole');
          updateTapholeSidebarThumb('taphole', '1 Taphole');
        }
        if (hasHingeStep) {
          state.hinge = 'Left';
          var hgStepLock = $q('[data-conditional-step="hinge"]');
          if (hgStepLock) hgStepLock.classList.add('vb-step--express-locked');
          hingeCards.forEach(function (c) {
            c.classList.toggle('vb-card--active', c.dataset.hinge === 'Left');
          });
          ['vb-sum-hinge', 'vb-sum-hinge-m'].forEach(function (id) {
            var el = $id(id);
            if (el) el.textContent = 'Left Hinged (door opens from the right)';
          });
          updateHingeSidebarThumb('Left');
        }
        applyExpressFinishFilter(true);
        updateSkuDisplay();
        updatePrice();
        updateATCState();
      });
    });

    /** Undo the express fixed-build locks - the customer is back in the
     *  standard flow where taphole (and hinge on minis) are real choices
     *  again, so both unlock and reset to unpicked. */
    var clearExpressBuildLocks = function () {
      if (hasFixedTapholeChoice) {
        state.taphole = null;
        state.tapholeTouched = false;
        var thStepUnlock = $q('[data-conditional-step="taphole"]');
        if (thStepUnlock) thStepUnlock.classList.remove('vb-step--express-locked');
        $qa('.vb-card--taphole').forEach(function (c) { c.classList.remove('vb-card--active'); });
        updateSummary('taphole', null);
        $qa('[data-summary="taphole"] .vb-sidebar__item-thumb-svg').forEach(function (el) { el.remove(); });
      }
      if (hasHingeStep) {
        state.hinge = null;
        var hgStepUnlock = $q('[data-conditional-step="hinge"]');
        if (hgStepUnlock) hgStepUnlock.classList.remove('vb-step--express-locked');
        hingeCards.forEach(function (c) { c.classList.remove('vb-card--active'); });
        ['vb-sum-hinge', 'vb-sum-hinge-m'].forEach(function (id) {
          var el = $id(id);
          if (el) el.textContent = '';
        });
        updateHingeSidebarThumb(null);
      }
    };

    // Any standard size-card click exits Express mode: restore the full
    // finish palette and drop the committed Express variant - the standard
    // flow re-resolves the variant from the bowl overlay. Registered after
    // the original size handler, so state.size already reflects the click.
    sizeCards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (state.dispatch !== 'Express Ship') return;
        state.dispatch = 'Standard';
        expressCards.forEach(function (c) { c.classList.remove('vb-card--active'); });
        applyExpressFinishFilter(false);
        clearExpressBuildLocks();
        // Sizes-only: the size handler (registered earlier) has already
        // committed the Standard variant for this click - keep it, same as
        // the bowl-card exit path. Bowl-flow vanities drop the variant so
        // the overlay re-resolves it.
        if (!sizesOnly) state.variantId = null;
        updateSummary('size', state.size + 'mm');
        updateSkuDisplay();
        updatePrice();
        updateATCState();
      });
    });

    // Single-size vanities (Zurich Mini) have no size cards - the bowl row
    // is clicked directly, so a bowl-card click must also exit Express mode.
    // Unlike the size-card exit, the variant is NOT dropped: the original
    // bowl handler (registered earlier) has already resolved the correct
    // Standard variant for this click.
    bowlCards.forEach(function (card) {
      card.addEventListener('click', function () {
        if (state.dispatch !== 'Express Ship') return;
        state.dispatch = 'Standard';
        expressCards.forEach(function (c) { c.classList.remove('vb-card--active'); });
        applyExpressFinishFilter(false);
        clearExpressBuildLocks();
        updateSummary('size', state.size + 'mm');
        updateSkuDisplay();
        updatePrice();
        updateATCState();
      });
    });
  }

  // ── Step 3: Benchtop ───────────────────────────────────────────
  const benchtopSwatches = $qa('[data-option-group="benchtop"] .vb-swatch');
  benchtopSwatches.forEach((swatch) => {
    swatch.addEventListener('click', () => {
      // Imperial Top et al. — when an integrated top with a benchtop
      // finish restriction is active (skipBasin = true, but step 3 not
      // collapsed and not in cabinet-only), the swatch click registers
      // the chosen finish for the integrated top. The variant ID stays
      // the integrated-top variant set by the bowl card click; we don't
      // exit integrated mode or re-resolve.
      if (state.skipBasin && !state.skipHardware && !state.skipBenchtop) {
        setActive(benchtopSwatches, swatch);
        state.benchtopName = swatch.dataset.benchtopName;
        state.benchtopMaterial = swatch.dataset.benchtopMaterial;
        var integratedBtImg = swatch.querySelector('img');
        updateSidebarThumb('benchtop', integratedBtImg ? integratedBtImg.src : '');
        var integratedShort = state.benchtopMaterial.replace(' Premium Solid Surface', '').replace(' Solid Surface', '');
        updateSummary('benchtop', integratedShort + ' - ' + state.benchtopName);
        updatePrice();
        updateATCState();
        return;
      }

      // Undo any Cabinet-Only UI state — picking a real benchtop means
      // Handle/Taphole/Basin are back in play.
      resetCabinetOnlyMode();
      setActive(benchtopSwatches, swatch);
      state.benchtopName = swatch.dataset.benchtopName;
      state.benchtopMaterial = swatch.dataset.benchtopMaterial;

      // Picking a regular benchtop swatch leaves any integrated-top or
      // Cabinet-Only mode: clear those flags + restore basin step.
      if (hasTopTypes || hasTopVariants) {
        resetIntegratedTopMode();
        state.thickness = null;
        state.thicknessSurcharge = 0;
        state._mouldedTapholeCode = undefined;

        // Hide taphole note if present
        var tapholeNote = $q('.vb-taphole-note');
        if (tapholeNote) tapholeNote.classList.add('d-none');

        // Restore basin step from "integrated" mode
        var basinStep = $q('[data-conditional-step="basin"]');
        if (basinStep) {
          basinStep.classList.remove('vb-basin-integrated');
        }
        state.basinKey = '';
        state.basinName = 'No Basin';
        state.basinHandle = '';
        state.basinVariantId = null;
        state.basinPrice = 0;
        state.basinFinish = null;
        state.basinTouched = false;
        $qa('.vb-card--basin').forEach(function (c) { c.classList.remove('vb-card--active'); });
        updateSummary('basin', null);

        // Set top type based on benchtop material. Cherry Pie + Durasein
        // share the same Shopify variant (no price delta — same as
        // Waverley's original behaviour). Caesarstone is its own variant
        // with the +25% surcharge baked in at Phase C, so resolving to
        // it here is what makes the price update.
        if (state.benchtopMaterial && state.benchtopMaterial.indexOf('Cherry Pie') !== -1) {
          state.topType = 'Cherry Pie';
        } else if (state.benchtopMaterial && state.benchtopMaterial.indexOf('Durasein') !== -1) {
          state.topType = 'Durasein';
        } else if (state.benchtopMaterial && state.benchtopMaterial.indexOf('Caesarstone') !== -1) {
          state.topType = 'Caesarstone';
        }
        resolveVariant();

        // Show thickness selector for Durasein only
        // Thickness selector: server-rendered + always visible. Lock the
        // section unless the active material is Durasein, but keep the
        // cards on screen so the customer knows the option exists.
        // Locked → no card is selected (a "selected 12mm" while locked
        // could mislead the customer into thinking their Cherry Pie top
        // is 12mm — Cherry Pie is actually 20mm). Unlocked + no prior
        // selection → fall back to default 12mm.
        var thicknessGrp = $q('[data-thickness-group]');
        if (thicknessGrp) {
          var isDurasein = state.benchtopMaterial && state.benchtopMaterial.indexOf('Durasein') !== -1;
          thicknessGrp.classList.toggle('vb-thickness-selector--locked', !isDurasein);
          var thicknessCardsEl = thicknessGrp.querySelectorAll('.vb-card--thickness');
          if (!isDurasein) {
            thicknessCardsEl.forEach(function (c) { c.classList.remove('vb-card--active'); });
            state.thickness = null;
            state.thicknessSurcharge = 0;
          } else if (!thicknessGrp.querySelector('.vb-card--thickness.vb-card--active')) {
            var defaultCard = thicknessGrp.querySelector('.vb-card--thickness[data-surcharge="0"]');
            if (defaultCard) {
              setActive(thicknessCardsEl, defaultCard);
              state.thickness = defaultCard.dataset.thickness;
              state.thicknessSurcharge = 0;
            }
          }
        }
      }

      var btImg = swatch.querySelector('img');
      updateSidebarThumb('benchtop', btImg ? btImg.src : '');
      // Shorten long material names for sidebar display
      var shortMaterial = state.benchtopMaterial.replace(' Premium Solid Surface', '').replace(' Solid Surface', '');
      updateSummary('benchtop', shortMaterial + ' - ' + state.benchtopName);
      updateSkuDisplay();
      filterBasinsByConfig();
      updateTapholeOptions();
      updatePrice();
      updateATCState();
    });
  });

  // Thickness cards — server-rendered, always visible. Click handlers
  // fire only when the section is unlocked (CSS pointer-events: none on
  // .vb-thickness-selector--locked blocks interactions automatically).
  // No card is pre-selected on init: the section is locked by default
  // and the swatch click handler picks the default 12mm card the first
  // time Durasein gets chosen.
  const thicknessCards = $qa('.vb-card--thickness');
  thicknessCards.forEach((btn) => {
    btn.addEventListener('click', () => {
      setActive(thicknessCards, btn);
      state.thickness = btn.dataset.thickness;
      state.thicknessSurcharge = parseInt(btn.dataset.surcharge, 10) * 100;
      updatePrice();
      updateATCState();
    });
  });

  // ── Step 4: Hardware (parent card + overlay pattern) ────────────
  var HANDLE_FINISH_COLOURS = {
    'Matte Black':       { bg: '#1a1a1a', border: '#1a1a1a' },
    'Matte White':       { bg: '#f0eded', border: '#ddd' },
    'Brushed Nickel':    { bg: '#b0b0aa', border: '#999' },
    'Brushed Brass':     { bg: '#d4b064', border: '#c2a050' },
    'Brushed Copper':    { bg: '#b87333', border: '#a0632b' },
    'Brushed Gunmetal':  { bg: '#5a5a5a', border: '#444' },
    'Chrome':            { bg: '#d4d4d4', border: '#bbb' },
    'Black':             { bg: '#2a2a2a', border: '#2a2a2a' },
    'White':             { bg: '#ffffff', border: '#ddd' },
    'Nickel':            { bg: '#b0b0aa', border: '#999' },
    'Rose Gold':         { bg: '#c78a6e', border: '#b07a5e' },
    'Tan':               { bg: '#c4956a', border: '#a87d55' },
    'Arctic White':      { bg: '#f5f5f0', border: '#ddd' },
    'Blossoming':        { bg: '#e0c8c0', border: '#cdb0a8' },
    'Iceberg':           { bg: '#dce8ea', border: '#c4d4d8' },
    'Navona':            { bg: '#d0c4b0', border: '#baa898' },
    'Nimbus':            { bg: '#c8c4c0', border: '#b0aca8' },
    'Pepper Mill':       { bg: '#5a5048', border: '#4a4038' },
    'Sakal':             { bg: '#a89888', border: '#988878' },
    'Terra Italia':      { bg: '#8a7060', border: '#7a6050' },
    'Winter Woods':      { bg: '#8a8478', border: '#7a7468' },
  };

  var ptoSvgDataUri = "data:image/svg+xml," + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none">'
    + '<circle cx="24" cy="24" r="16" stroke="#c41230" stroke-width="1.5" stroke-dasharray="4 2.5" opacity="0.5"/>'
    + '<circle cx="24" cy="24" r="10" stroke="#c41230" stroke-width="1.5"/>'
    + '<circle cx="24" cy="24" r="4" fill="#c41230"/>'
    + '</svg>'
  );

  const hardwareCards = $qa('[data-option-group="hardware"] .vb-card--hardware');

  /** Parse the finishes JSON from a card's data attribute */
  function getCardFinishes(card) {
    var raw = card.dataset.handleFinishes;
    if (!raw) return [];
    try { return JSON.parse(raw); } catch (e) { return []; }
  }

  /** Add swatch dots to multi-finish handle cards */
  function addHandleFinishBadges() {
    hardwareCards.forEach(function (card) {
      var finishes = getCardFinishes(card);
      if (finishes.length <= 1) return;
      var old = card.querySelector('.vb-card__swatches');
      if (old) old.remove();
      var wrapper = document.createElement('div');
      wrapper.className = 'vb-card__swatches';
      finishes.forEach(function (f) {
        var dot = document.createElement('span');
        dot.className = 'vb-card__swatch-dot';
        var colours = HANDLE_FINISH_COLOURS[f.name] || { bg: '#ccc', border: '#bbb' };
        dot.style.backgroundColor = colours.bg;
        dot.style.borderColor = colours.border;
        dot.title = f.name;
        wrapper.appendChild(dot);
      });
      card.appendChild(wrapper);
      // Store original image src for restore
      var img = card.querySelector('.vb-card__img');
      if (img && !card.dataset.originalImgSrc) {
        card.dataset.originalImgSrc = img.src;
      }
    });
  }

  addHandleFinishBadges();

  /** Restore non-selected handle cards to default thumbnail */
  function restoreHandleCardImages(skipCard) {
    hardwareCards.forEach(function (c) {
      if (c === skipCard) return;
      var origSrc = c.dataset.originalImgSrc;
      if (origSrc) {
        var img = c.querySelector('.vb-card__img');
        if (img) img.src = origSrc;
      }
      c.querySelectorAll('.vb-card__swatch-dot--active').forEach(function (dot) {
        dot.classList.remove('vb-card__swatch-dot--active');
      });
      updateHandleCardFinishLabel(c, null);
    });
  }

  /** Show/clear finish name label below handle name on card */
  function updateHandleCardFinishLabel(card, finishName) {
    var label = card.querySelector('.vb-card__label');
    if (!label) return;
    var existing = label.querySelector('.vb-card__finish-label');
    if (!finishName) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('span');
      existing.className = 'vb-card__finish-label';
      label.appendChild(existing);
    }
    existing.textContent = finishName;
  }

  /** Select a handle directly (called for push-to-open, single-finish, or from overlay) */
  function selectHandle(card, finish) {
    restoreHandleCardImages(card);
    setActive(hardwareCards, card);

    state.hardwareType = card.dataset.hardwareType;
    state.hardware = card.dataset.hardwareName;
    state.handleFinish = finish ? finish.name : null;
    state.handleProductHandle = (finish && finish.product_handle) ? finish.product_handle : null;
    state.handleCategory = card.dataset.handleCategory || null;

    if (finish && finish.img) {
      var filesBase = builder.dataset.filesBase || '';
      var imgUrl = filesBase + finish.img;
      var cardImg = card.querySelector('.vb-card__img');
      if (cardImg) {
        cardImg.src = imgUrl;
        cardImg.alt = state.hardware + ' - ' + finish.name;
      }
      updateSidebarThumb('hardware', imgUrl);

      // Highlight swatch dot
      card.querySelectorAll('.vb-card__swatch-dot').forEach(function (dot) {
        dot.classList.toggle('vb-card__swatch-dot--active', dot.title === finish.name);
      });
      updateHandleCardFinishLabel(card, finish.name);
    } else if (state.hardwareType === 'push-to-open' || state.hardwareType === 'finger-pull') {
      updateSidebarThumb('hardware', ptoSvgDataUri);
    } else {
      var hwImg = card.querySelector('img');
      updateSidebarThumb('hardware', hwImg ? hwImg.src : '');
    }

    if (state.handleFinish) {
      updateSummary('hardware', state.hardware + ' - ' + state.handleFinish);
    } else {
      updateSummary('hardware', state.hardware);
    }
    updateHandleQtyBadge();
    updatePrice();
    updateATCState();
  }

  /** Close the handle finish popover */
  function closeHandleOverlay(animate) {
    var popover = $id('vb-handle-finish-popover');
    if (!popover) return;
    if (popover._hoverCard) {
      popover._hoverCard.removeEventListener('mouseenter', cancelHandlePopoverClose);
      popover._hoverCard.removeEventListener('mouseleave', startHandlePopoverClose);
    }
    document.removeEventListener('click', handleHandlePopoverOutsideClick);
    cancelHandlePopoverClose();
    if (animate) {
      popover.classList.add('vb-finish-popover--closing');
      setTimeout(function () { if (popover.parentNode) popover.remove(); }, 200);
    } else {
      popover.remove();
    }
  }

  var handlePopoverCloseTimer = null;
  function startHandlePopoverClose() {
    if (handlePopoverCloseTimer) return;
    handlePopoverCloseTimer = setTimeout(function () {
      handlePopoverCloseTimer = null;
      closeHandleOverlay(true);
    }, 400);
  }
  function cancelHandlePopoverClose() {
    if (handlePopoverCloseTimer) { clearTimeout(handlePopoverCloseTimer); handlePopoverCloseTimer = null; }
  }

  function handleHandlePopoverOutsideClick(e) {
    var popover = $id('vb-handle-finish-popover');
    if (!popover) return;
    if (popover.contains(e.target)) return;
    if (e.target.closest('.vb-card--hardware')) return;
    closeHandleOverlay(true);
  }

  /** Open the handle finish selection overlay */
  function openHandleFinishOverlay(card, finishes) {
    closeHandleOverlay();
    closeBasinOverlay(); // Close basin overlay if open

    var hwName = card.dataset.hardwareName;

    var popover = document.createElement('div');
    popover.className = 'vb-finish-popover';
    popover.id = 'vb-handle-finish-popover';

    var header = document.createElement('div');
    header.className = 'vb-finish-popover__header';
    header.innerHTML = '<h3 class="vb-finish-popover__title">' + hwName + ' — Select Finish</h3>'
      + '<button type="button" class="vb-finish-popover__close" aria-label="Close">'
      + '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      + '</button>';
    header.querySelector('.vb-finish-popover__close').addEventListener('click', function (e) {
      e.stopPropagation();
      closeHandleOverlay();
    });
    popover.appendChild(header);

    var grid = document.createElement('div');
    grid.className = 'vb-finish-popover__grid';

    var filesBase = builder.dataset.filesBase || '';

    finishes.forEach(function (f) {
      var finishCard = document.createElement('button');
      finishCard.type = 'button';
      finishCard.className = 'vb-finish-card';
      finishCard.dataset.finish = f.name;

      var html = '';
      if (f.img) {
        html += '<img src="' + filesBase + f.img + '" alt="' + f.name + '" class="vb-finish-card__img" loading="lazy">';
      }
      html += '<div class="vb-finish-card__name">' + f.name + '</div>';
      finishCard.innerHTML = html;

      finishCard.addEventListener('click', function (e) {
        e.stopPropagation();
        selectHandle(card, f);
        closeHandleOverlay();
      });

      grid.appendChild(finishCard);
    });

    var isMobile = window.innerWidth <= 575;
    var maxCols = isMobile ? 3 : 4;
    var cols = Math.min(finishes.length, maxCols);
    var colWidth = isMobile ? '1fr' : '140px';
    grid.style.gridTemplateColumns = 'repeat(' + cols + ', ' + colWidth + ')';

    popover.appendChild(grid);

    setTimeout(function () {
      document.addEventListener('click', handleHandlePopoverOutsideClick);
    }, 100);

    var gridContainer = card.closest('.vb-cards');
    if (gridContainer) {
      gridContainer.style.position = 'relative';
      gridContainer.appendChild(popover);
    } else {
      card.parentElement.appendChild(popover);
    }

    var cardOffsetTop = card.offsetTop + card.offsetHeight + 8;
    popover.style.top = cardOffsetTop + 'px';

    // Only bind hover-to-close on devices with a real pointer. Touch devices
    // synthesize a mouseleave as soon as the tap ends, which would immediately
    // dismiss the popover the user just opened.
    if (window.matchMedia('(hover: hover)').matches) {
      popover.addEventListener('mouseenter', function () { cancelHandlePopoverClose(); });
      popover.addEventListener('mouseleave', function () { startHandlePopoverClose(); });
      card.addEventListener('mouseenter', cancelHandlePopoverClose);
      card.addEventListener('mouseleave', startHandlePopoverClose);
    }
    popover._hoverCard = card;

    setTimeout(function () {
      var stickyHeader = document.querySelector('.section-header, .shopify-section-header, header');
      var headerHeight = stickyHeader ? stickyHeader.offsetHeight : 0;
      var viewportHeight = window.innerHeight - headerHeight;
      var cardHeight = card.offsetHeight;
      var popoverHeight = popover.offsetHeight;
      var combinedHeight = cardHeight + 8 + popoverHeight;
      var targetOffset = (viewportHeight - combinedHeight) / 2;
      if (targetOffset < 20) targetOffset = 20;
      var scrollY = card.getBoundingClientRect().top + window.pageYOffset - headerHeight - targetOffset;
      window.scrollTo({ top: scrollY, behavior: 'smooth' });
    }, 80);
  }

  // Click handler: branch by finish count
  hardwareCards.forEach(function (card) {
    card.addEventListener('click', function () {
      var hwType = card.dataset.hardwareType;

      if (hwType === 'push-to-open') {
        closeHandleOverlay();
        selectHandle(card, null);
        return;
      }

      var finishes = getCardFinishes(card);

      if (finishes.length === 0) {
        closeHandleOverlay();
        selectHandle(card, null);
      } else if (finishes.length === 1) {
        closeHandleOverlay();
        selectHandle(card, finishes[0]);
      } else {
        openHandleFinishOverlay(card, finishes);
      }
    });
  });

  // ── Step 5: Basin ──────────────────────────────────────────────
  const basinCards = $qa('[data-option-group="basin"] .vb-card');

  basinCards.forEach((card) => {
    card.addEventListener('click', () => {
      var key = card.dataset.basinKey || '';
      var hasMultipleFinishes = finishesData && key && finishesData[key] && finishesData[key].finishes.length > 1;

      if (hasMultipleFinishes) {
        // Open overlay for finish selection — don't mark active until a finish is picked
        openBasinFinishOverlay(key, card);
      } else {
        // Direct selection (No Basin, or single-finish basin)
        selectBasin(card, key);
      }
    });
  });

  /** Select a basin directly (no overlay) */
  /** Restore all basin card images to their original metaobject images */
  function restoreBasinCardImages(skipCard) {
    basinCards.forEach(function (c) {
      if (c === skipCard) return;
      var origSrc = c.dataset.originalImgSrc;
      if (origSrc) {
        var img = c.querySelector('.vb-card__img');
        if (img) img.src = origSrc;
      }
      // Clear active swatch dots
      c.querySelectorAll('.vb-card__swatch-dot--active').forEach(function (dot) {
        dot.classList.remove('vb-card__swatch-dot--active');
      });
      // Clear finish label
      updateBasinCardFinishLabel(c, null);
    });
  }

  /** Show or clear the selected finish name inside the basin name label */
  function updateBasinCardFinishLabel(card, finishName) {
    var label = card.querySelector('.vb-card__label');
    if (!label) return;
    var existing = label.querySelector('.vb-card__finish-label');
    if (!finishName) {
      if (existing) existing.remove();
      return;
    }
    if (!existing) {
      existing = document.createElement('span');
      existing.className = 'vb-card__finish-label';
      label.appendChild(existing);
    }
    existing.textContent = finishName;
  }

  /** Update basin summary and x2 badge based on double bowl state */
  function updateBasinDisplay(activeCard) {
    if (!state.basinKey) {
      // "No Basin" selected or nothing selected
      updateSummary('basin', state.basinTouched ? 'No Basin' : null);
      removeBasinQtyBadges();
      return;
    }
    var suffix = state.isDouble ? ' (x2)' : '';
    updateSummary('basin', state.basinName + suffix);

    // Toggle x2 badge on the active basin card
    removeBasinQtyBadges();
    if (state.isDouble) {
      var card = activeCard || $q('.vb-card--basin.vb-card--active');
      if (card && !card.querySelector('.vb-card__qty-badge')) {
        var badge = document.createElement('span');
        badge.className = 'vb-card__qty-badge';
        badge.textContent = 'x2';
        card.appendChild(badge);
      }
    }
  }

  function removeBasinQtyBadges() {
    $qa('.vb-card--basin .vb-card__qty-badge').forEach(function (b) { b.remove(); });
  }

  /** Update P&W summary (with " (x2)" suffix on double-bowl) and the x2
   *  badge on the active P&W card. Mirrors updateBasinDisplay — the badge
   *  makes the cart quantity visually obvious, matching what resolvePlugWaste
   *  actually submits (qty = isDouble ? 2 : 1). */
  function updatePlugWasteDisplay() {
    $qa('.vb-card--pw .vb-card__qty-badge').forEach(function (b) { b.remove(); });
    if (!state.pwFinish) {
      updateSummary('pw', state.pwTouched ? 'No Plug & Waste' : null);
      return;
    }
    var suffix = state.isDouble ? ' (x2)' : '';
    updateSummary('pw', state.pwName + suffix);
    if (state.isDouble) {
      var card = $q('.vb-card--pw.vb-card--active');
      if (card && !card.querySelector('.vb-card__qty-badge')) {
        var badge = document.createElement('span');
        badge.className = 'vb-card__qty-badge';
        badge.textContent = 'x2';
        card.appendChild(badge);
      }
    }
  }

  /** Show or clear the "xN" quantity badge on the selected handle card, where
   *  N is the handle count for the current size/cabinet. Mirrors the basin x2
   *  badge pattern so users can see how many physical handles the selection
   *  will add at checkout. */
  function updateHandleQtyBadge() {
    $qa('.vb-card--hardware .vb-card__qty-badge').forEach(function (b) { b.remove(); });
    var activeHandle = $q('.vb-card--hardware.vb-card--active');
    if (!activeHandle) return;
    if (state.hardwareType === 'push-to-open' || state.hardwareType === 'finger-pull') return;
    var count = getHandleCount();
    if (count > 1) {
      var badge = document.createElement('span');
      badge.className = 'vb-card__qty-badge';
      badge.textContent = 'x' + count;
      activeHandle.appendChild(badge);
    }
  }

  function selectBasin(card, key) {
    setActive(basinCards, card);
    state.basinTouched = true;
    state.basinHandle = card.dataset.basinHandle;
    state.basinKey = key;
    state.basinName = card.dataset.basinName;
    state.basinFinish = null;
    state.basinVariantId = null;
    state.basinPrice = 0;

    // Restore all card images to defaults when selecting No Basin or switching
    restoreBasinCardImages();

    // If single finish, store the product info for cart
    if (finishesData && key && finishesData[key] && finishesData[key].finishes.length === 1) {
      var f = finishesData[key].finishes[0];
      state.basinHandle = f.handle;
      state.basinVariantId = f.variant_id;
      state.basinFinish = f.finish;
      state.basinPrice = Math.round(parseFloat(f.price) * 100);
      state.basinName = finishesData[key].name + ' - ' + f.finish;
    }

    // Update sidebar thumbnail
    var basinImg = card.querySelector('.vb-card__img');
    updateSidebarThumb('basin', state.basinKey && basinImg ? basinImg.src : '');

    updateBasinDisplay(card);
    updateTapholeOptions();
    updatePrice();
    updateATCState();
  }

  /** Open the basin finish selection overlay */
  function openBasinFinishOverlay(key, card) {
    var data = finishesData[key];
    if (!data) return;

    // Close any existing popover first
    closeBasinOverlay();

    // Build the popover element
    var popover = document.createElement('div');
    popover.className = 'vb-finish-popover';
    popover.id = 'vb-finish-popover';

    var header = document.createElement('div');
    header.className = 'vb-finish-popover__header';
    header.innerHTML = '<h3 class="vb-finish-popover__title">' + data.name + ' — Select Finish</h3>'
      + '<button type="button" class="vb-finish-popover__close" aria-label="Close">'
      + '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      + '</button>';
    header.querySelector('.vb-finish-popover__close').addEventListener('click', function (e) {
      e.stopPropagation();
      closeBasinOverlay();
    });
    popover.appendChild(header);

    var grid = document.createElement('div');
    grid.className = 'vb-finish-popover__grid';

    // Sort finishes by price (cheapest first), then standard finishes before specialty within same price
    var standardFinishes = VB_CONFIG.standardFinishSortOrder || ['Gloss White', 'Matte White', 'Matte Black', 'Black Out/White In'];
    var sortedFinishes = data.finishes.slice().sort(function (a, b) {
      var priceDiff = parseFloat(a.price) - parseFloat(b.price);
      if (priceDiff !== 0) return priceDiff;
      var aStd = standardFinishes.indexOf(a.finish);
      var bStd = standardFinishes.indexOf(b.finish);
      var aRank = aStd >= 0 ? aStd : 100;
      var bRank = bStd >= 0 ? bStd : 100;
      return aRank - bRank;
    });

    sortedFinishes.forEach(function (f) {
      var finishCard = document.createElement('button');
      finishCard.type = 'button';
      finishCard.className = 'vb-finish-card';
      finishCard.dataset.handle = f.handle;
      finishCard.dataset.variantId = f.variant_id;
      finishCard.dataset.finish = f.finish;
      finishCard.dataset.price = f.price;

      var html = '';
      // "X% OFF" sale pill (top-left). Computed from this finish's
      // compare_at_price vs price so the percentage matches the actual
      // discount on this specific variant.
      var fCap = f.compare_at_price != null ? parseFloat(f.compare_at_price) : 0;
      var fPrice = parseFloat(f.price);
      if (fCap > fPrice && fPrice > 0) {
        var fPct = Math.round((fCap - fPrice) * 100 / fCap);
        html += '<span class="vb-finish-card__sale-pill">' + fPct + '% OFF</span>';
      }
      var thumbUrl = f.thumb ? (builder.dataset.assetBase || '') + f.thumb : f.image;
      if (thumbUrl) {
        html += '<img src="' + thumbUrl + '" alt="' + f.finish + '" class="vb-finish-card__img" loading="lazy">';
      }
      html += '<div class="vb-finish-card__name">' + f.finish + '</div>';
      html += '<div class="vb-finish-card__price">$' + parseFloat(f.price).toFixed(0);
      if (fCap > fPrice && fPrice > 0) {
        html += '<span class="vb-finish-card__price-was">$' + Math.round(fCap) + '</span>';
      }
      html += '</div>';
      finishCard.innerHTML = html;

      finishCard.addEventListener('click', function (e) {
        e.stopPropagation();

        // Select this finish
        state.basinTouched = true;
        state.basinHandle = f.handle;
        state.basinKey = key;
        state.basinVariantId = f.variant_id;
        state.basinFinish = f.finish;
        state.basinPrice = Math.round(parseFloat(f.price) * 100);
        state.basinName = data.name + ' - ' + f.finish;

        // Restore other cards now that a finish is actually committed
        restoreBasinCardImages(card);

        setActive(basinCards, card);

        // Update the basin card image to show the selected finish
        var selectedThumb = f.thumb ? (builder.dataset.assetBase || '') + f.thumb : f.image;
        if (selectedThumb) {
          var cardImg = card.querySelector('.vb-card__img');
          if (cardImg) {
            cardImg.src = selectedThumb;
            cardImg.alt = data.name + ' - ' + f.finish;
          }
        }

        // Highlight the selected swatch dot
        var swatches = card.querySelectorAll('.vb-card__swatch-dot');
        swatches.forEach(function (dot) {
          dot.classList.toggle('vb-card__swatch-dot--active', dot.title === f.finish);
        });

        // Show finish name below basin name on the card
        updateBasinCardFinishLabel(card, f.finish);

        // Update sidebar thumbnail with selected finish
        var thumbForSidebar = f.thumb ? (builder.dataset.assetBase || '') + f.thumb : f.image;
        updateSidebarThumb('basin', thumbForSidebar || '');

        updateBasinDisplay(card);
        updateTapholeOptions();
        updatePrice();
        updateATCState();

        // Close popover
        closeBasinOverlay();
      });

      grid.appendChild(finishCard);
    });

    // Set grid columns based on finish count (max 4 per row, max 3 on mobile)
    var isMobile = window.innerWidth <= 575;
    var maxCols = isMobile ? 3 : 4;
    var cols = Math.min(sortedFinishes.length, maxCols);
    var colWidth = isMobile ? '1fr' : '140px';
    grid.style.gridTemplateColumns = 'repeat(' + cols + ', ' + colWidth + ')';

    popover.appendChild(grid);

    // Click-outside-to-close via document listener
    setTimeout(function () {
      document.addEventListener('click', handlePopoverOutsideClick);
    }, 100);

    // Position the popover relative to the card's grid container
    var gridContainer = card.closest('.vb-cards');
    if (gridContainer) {
      gridContainer.style.position = 'relative';
    }

    // Insert popover into the grid container so it floats over the grid
    var cardRect = card.getBoundingClientRect();
    var gridRect = gridContainer ? gridContainer.getBoundingClientRect() : cardRect;

    if (gridContainer) {
      gridContainer.appendChild(popover);
    } else {
      card.parentElement.appendChild(popover);
    }

    // Position below the card
    var cardOffsetTop = card.offsetTop + card.offsetHeight + 8;
    popover.style.top = cardOffsetTop + 'px';

    // Auto-close on mouse leave — pointer devices only. Touch taps synthesize
    // a mouseleave immediately after opening, which would instantly dismiss.
    if (window.matchMedia('(hover: hover)').matches) {
      popover.addEventListener('mouseenter', function () { cancelPopoverClose(); });
      popover.addEventListener('mouseleave', function () { startPopoverClose(); });
      card.addEventListener('mouseenter', cancelPopoverClose);
      card.addEventListener('mouseleave', startPopoverClose);
    }
    // Store ref to card so we can clean up listeners on close
    popover._hoverCard = card;

    // Smooth-scroll to center the card + popover as a unit in the viewport
    setTimeout(function () {
      var header = document.querySelector('.section-header, .shopify-section-header, header');
      var headerHeight = header ? header.offsetHeight : 0;
      var viewportHeight = window.innerHeight - headerHeight;
      var cardHeight = card.offsetHeight;
      var popoverHeight = popover.offsetHeight;
      var combinedHeight = cardHeight + 8 + popoverHeight;
      var targetOffset = (viewportHeight - combinedHeight) / 2;
      if (targetOffset < 20) targetOffset = 20; // minimum gap from header
      var scrollY = card.getBoundingClientRect().top + window.pageYOffset - headerHeight - targetOffset;
      window.scrollTo({ top: scrollY, behavior: 'smooth' });
    }, 80);
  }

  var popoverCloseTimer = null;
  function startPopoverClose() {
    if (popoverCloseTimer) return;
    popoverCloseTimer = setTimeout(function () {
      popoverCloseTimer = null;
      closeBasinOverlay(true);
    }, 400);
  }
  function cancelPopoverClose() {
    if (popoverCloseTimer) { clearTimeout(popoverCloseTimer); popoverCloseTimer = null; }
  }

  /** Close the basin finish popover, with optional fade-out animation */
  function closeBasinOverlay(animate) {
    var popover = $id('vb-finish-popover');
    if (!popover) return;
    // Clean up hover listeners on the card
    if (popover._hoverCard) {
      popover._hoverCard.removeEventListener('mouseenter', cancelPopoverClose);
      popover._hoverCard.removeEventListener('mouseleave', startPopoverClose);
    }
    document.removeEventListener('click', handlePopoverOutsideClick);
    cancelPopoverClose();
    if (animate) {
      popover.classList.add('vb-finish-popover--closing');
      setTimeout(function () { if (popover.parentNode) popover.remove(); }, 200);
    } else {
      popover.remove();
    }
  }

  /** Handle clicks outside the popover to close it */
  function handlePopoverOutsideClick(e) {
    var popover = $id('vb-finish-popover');
    if (!popover) return;
    if (popover.contains(e.target)) return;
    if (e.target.closest('.vb-card--basin')) return;
    closeBasinOverlay(true);
  }

  // Basin remove buttons in sidebar
  $qa('.vb-sidebar__item-remove[data-remove="basin"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      var noBasin = $q('.vb-card--basin[data-basin-handle=""]');
      if (noBasin) noBasin.click();
    });
  });

  // ── Step: Hinge Direction ──────────────────────────────────────
  // Renders for vanities whose config has offers_hinge_selection=true.
  // Captures customer's L/R hinge preference as a "Door Hinge" line item
  // property on add-to-cart. No SKU/price effect — fulfilled to spec.
  const hingeCards = $qa('[data-option-group="hinge"] .vb-card');
  hingeCards.forEach((card) => {
    card.addEventListener('click', () => {
      setActive(hingeCards, card);
      state.hinge = card.dataset.hinge;
      // Sidebar sync — populate the "Hinge Direction" row value on both
      // desktop and mobile sidebars.
      var hingeLabel = state.hinge === 'Left'
        ? 'Left Hinged (door opens from right)'
        : 'Right Hinged (door opens from left)';
      ['vb-sum-hinge', 'vb-sum-hinge-m'].forEach(function (id) {
        var el = $id(id);
        if (el) el.textContent = hingeLabel;
      });
      updateHingeSidebarThumb(state.hinge);
      updateATCState();
    });
  });

  // ── Step: Drawer Side ──────────────────────────────────────────
  // Renders for vanities whose config lists drawer_side_option_values: the
  // option1 values (e.g. "750mm Centre Bowl") built either left- or
  // right-hand to order (Zurich family centre-bowl drawer sizes,
  // supplier-confirmed the standard range builds either LHD or RHD). Every
  // other size implies its own answer - derived below - so the "Drawer Side"
  // line item property is emitted on EVERY order of a drawer-configured
  // vanity and the team never infers a build from bowl position. No
  // SKU/price impact; fulfilled to the order-form note.
  const drawerCards = $qa('[data-option-group="drawer"] .vb-card');

  /** Derived drawer side for sizes where the build implies it. Offset
   *  bowls carry the drawer stack opposite the bowl (the basin waste drops
   *  through the door section - the L/R spec sheets mirror accordingly),
   *  doubles have a centre stack between the basins, and the remaining
   *  sizes (600s) are all-door. */
  function deriveAutoDrawer(opt1) {
    if (opt1.indexOf('Left') !== -1) return 'Right-hand';
    if (opt1.indexOf('Right') !== -1) return 'Left-hand';
    if (opt1.indexOf('Double') !== -1) return 'Centre';
    return 'None (all-door unit)';
  }

  /** The value the "Drawer Side" property will carry, or null while a
   *  choice size awaits the customer's pick (ATC stays locked until then).
   *  Express is a fixed stocked build: right-hand drawers. */
  function resolveDrawerSide() {
    if (!hasDrawerFeature || !state.size || !state.bowl) return null;
    var opt1 = state.size + 'mm ' + state.bowl;
    var isChoice = drawerSideValues.indexOf(opt1) !== -1;
    if (state.dispatch === 'Express Ship') return isChoice ? 'Right-hand' : deriveAutoDrawer(opt1);
    if (isChoice) return state.drawer;
    return deriveAutoDrawer(opt1);
  }

  /** Front-view cabinet thumbs for the sidebar Drawer Side row. No accent
   *  marks (Chris): the range is finger-pull so anything bar-like reads as
   *  a handle - the stacked-front seam vs the plain door already says
   *  which side the drawers sit. */
  var DRAWER_THUMB_SVGS = (function () {
    function seg(x, w, isStack) {
      if (!isStack) return '<rect x="' + x + '" y="10" width="' + w + '" height="46" fill="#f4f4f4" stroke="#888" stroke-width="1"/>';
      return '<rect x="' + x + '" y="10" width="' + w + '" height="22" fill="#f4f4f4" stroke="#888" stroke-width="1"/>'
        + '<rect x="' + x + '" y="34" width="' + w + '" height="22" fill="#f4f4f4" stroke="#888" stroke-width="1"/>';
    }
    function svg(inner) { return '<svg viewBox="4 4 92 60"><rect x="8" y="8" width="84" height="50" fill="#e0e0e0"/>' + inner + '</svg>'; }
    return {
      'Left-hand': svg(seg(10, 32, true) + seg(44, 46, false)),
      'Right-hand': svg(seg(10, 46, false) + seg(58, 32, true)),
      'Centre': svg(seg(10, 24, false) + seg(36, 28, true) + seg(66, 24, false)),
      'None (all-door unit)': svg(seg(10, 39, false) + seg(51, 39, false)),
    };
  })();

  function updateDrawerSidebarThumb(val) {
    var svgHtml = val ? DRAWER_THUMB_SVGS[val] : null;
    ['vb-thumb-drawer', 'vb-thumb-drawer-m'].forEach(function (id) {
      var imgEl = $id(id);
      if (!imgEl) return;
      var existing = imgEl.parentNode.querySelector('.vb-sidebar__item-thumb-svg');
      if (existing) existing.remove();
      imgEl.style.display = 'none';
      if (!svgHtml) return;
      var wrapper = document.createElement('div');
      wrapper.innerHTML = svgHtml;
      var svg = wrapper.firstChild;
      svg.classList.add('vb-sidebar__item-thumb-svg');
      svg.style.width = '48px';
      svg.style.height = '48px';
      svg.style.borderRadius = '6px';
      svg.style.flexShrink = '0';
      svg.style.background = '#f8f8f8';
      svg.style.border = '1px solid #eee';
      imgEl.parentNode.insertBefore(svg, imgEl);
    });
  }

  /** Front-view single-door thumbs for the sidebar Hinge Direction row -
   *  crimson hinge pads mark the hinged edge, matching the step cards'
   *  visual language. The hinge row always had a thumb slot but never a
   *  thumb (pre-dates the taphole/drawer thumb pattern) - this also fills
   *  it in for the pre-Zurich hinge vanities (Charlie, Petite, Tiny, Seek). */
  var HINGE_THUMB_SVGS = (function () {
    // Portrait proportions - hinge selection lives on narrow single-door
    // units (Zurich Mini 460x850, Charlie/Petite/Tiny/Seek), so a wide
    // landscape cabinet reads stretched in the square thumb frame.
    function door(hingeX) {
      return '<svg viewBox="0 0 60 60"><rect x="17" y="5" width="26" height="50" fill="#e0e0e0"/>'
        + '<rect x="19" y="7" width="22" height="46" fill="#f4f4f4" stroke="#888" stroke-width="1"/>'
        + '<rect x="' + hingeX + '" y="14" width="3" height="8" rx="1" fill="#c41230"/>'
        + '<rect x="' + hingeX + '" y="38" width="3" height="8" rx="1" fill="#c41230"/></svg>';
    }
    return { 'Left': door(19.5), 'Right': door(37.5) };
  })();

  function updateHingeSidebarThumb(val) {
    var svgHtml = val ? HINGE_THUMB_SVGS[val] : null;
    ['vb-thumb-hinge', 'vb-thumb-hinge-m'].forEach(function (id) {
      var imgEl = $id(id);
      if (!imgEl) return;
      var existing = imgEl.parentNode.querySelector('.vb-sidebar__item-thumb-svg');
      if (existing) existing.remove();
      imgEl.style.display = 'none';
      if (!svgHtml) return;
      var wrapper = document.createElement('div');
      wrapper.innerHTML = svgHtml;
      var svg = wrapper.firstChild;
      svg.classList.add('vb-sidebar__item-thumb-svg');
      svg.style.width = '48px';
      svg.style.height = '48px';
      svg.style.borderRadius = '6px';
      svg.style.flexShrink = '0';
      svg.style.background = '#f8f8f8';
      svg.style.border = '1px solid #eee';
      imgEl.parentNode.insertBefore(svg, imgEl);
    });
  }

  function updateDrawerSummary() {
    var val = resolveDrawerSide();
    var text = '';
    if (val) text = val === 'None (all-door unit)' ? val : val + ' drawers';
    ['vb-sum-drawer', 'vb-sum-drawer-m'].forEach(function (id) {
      var el = $id(id);
      if (el) el.textContent = text;
    });
    updateDrawerSidebarThumb(val);
  }

  /** Show the Drawer Side step whenever a choice size is selected - live
   *  on a Standard build, express-locked (Right-hand ticked, Left-hand
   *  dimmed inert) on an Express build so the customer still sees the
   *  choice a Standard build offers. Sidebar row stays in sync on every
   *  state change. Called from updateATCState - the chokepoint every
   *  selection path already runs through - so express entry/exit and the
   *  bowl overlay need no extra wiring. Cards queried fresh (not the
   *  module-level const) because updateATCState can run before the
   *  drawer module's declarations execute at init. */
  function updateDrawerSideUI() {
    var opt1 = (state.size && state.bowl) ? state.size + 'mm ' + state.bowl : null;
    var isChoiceSize = !!opt1 && drawerSideValues.indexOf(opt1) !== -1;
    var lockedExpress = isChoiceSize && state.dispatch === 'Express Ship';
    toggleConditionalStep('drawer', isChoiceSize);
    var step = $q('[data-conditional-step="drawer"]');
    if (step) step.classList.toggle('vb-step--express-locked', lockedExpress);
    // Ticks derive from the resolved value, so the express lock-in and a
    // customer's own standard pick render identically.
    var resolved = lockedExpress ? 'Right-hand' : state.drawer;
    $qa('[data-option-group="drawer"] .vb-card').forEach(function (c) {
      c.classList.toggle('vb-card--active', !!resolved && c.dataset.drawer === resolved);
    });
    updateDrawerSummary();
  }

  drawerCards.forEach((card) => {
    card.addEventListener('click', () => {
      setActive(drawerCards, card);
      state.drawer = card.dataset.drawer;
      updateATCState();
    });
  });

  // ── Step: Hinging (Standard / Surround) ────────────────────────
  // Zurich Shaving Cabinet: 1500/1800 come with Standard or Surround
  // hinging (two spec sheets each, same order code - order-form
  // choice). Other sizes are fixed Standard, derived below, so the
  // "Hinging" line item property is emitted on EVERY order of a
  // hinging-configured product - same never-infer rule as Drawer Side.
  // No SKU/price impact; fulfilled to the order-form note.
  const hingingCards = $qa('[data-option-group="hinging-choice"] .vb-card');

  /** The current option1 lookup key: plain "600mm" on sizes-only
   *  products, "{size}mm {bowl}" (+ cabinet type) elsewhere. */
  function vbOption1Key() {
    if (!state.size) return null;
    if (sizesOnly) return state.size + 'mm';
    if (!state.bowl) return null;
    var k = state.size + 'mm ' + state.bowl;
    if (state.cabinetType) k += ' (' + state.cabinetType + ')';
    return k;
  }

  /** The value the "Hinging" property will carry, or null while a choice
   *  size awaits the customer's pick (ATC stays locked until then). */
  function resolveHinging() {
    if (!hasHingingFeature) return null;
    var key = vbOption1Key();
    if (!key) return null;
    if (hingingValues.indexOf(key) !== -1) return state.hinging;
    return 'Standard';
  }

  function updateHingingSummary() {
    var val = resolveHinging();
    var text = val ? val + ' Hinging' : '';
    ['vb-sum-hinging', 'vb-sum-hinging-m'].forEach(function (id) {
      var el = $id(id);
      if (el) el.textContent = text;
    });
  }

  /** The Hinging step stays VISIBLE at all times (Chris: customers should
   *  discover the Surround option - it may sway them toward a larger
   *  size). It locks (vb-step--choice-locked: dimmed inert cards + hint)
   *  whenever the choice does not apply: before a size is picked (no
   *  tick) and on fixed-Standard sizes including every Express size
   *  (Standard ticked - it IS the build). Unlocks as a required pick on
   *  the hinging_option_values sizes. Called from updateATCState (the
   *  chokepoint); cards queried fresh to dodge init-order issues. */
  function updateHingingUI() {
    var key = vbOption1Key();
    var choiceActive = !!key && hingingValues.indexOf(key) !== -1;
    var step = $q('[data-conditional-step="hinging"]');
    if (step) step.classList.toggle('vb-step--choice-locked', !choiceActive);
    // Swap the Standard card to its 2-door icon while a fixed-Standard
    // 2-door size is selected (600-1200); pre-selection and the 3-door
    // choice sizes show the 3-door drawing.
    if (step) step.classList.toggle('vb-step--hinging-2door', !!key && !choiceActive);
    var resolved = choiceActive ? state.hinging : (key ? 'Standard' : null);
    $qa('[data-option-group="hinging-choice"] .vb-card').forEach(function (c) {
      c.classList.toggle('vb-card--active', !!resolved && c.dataset.hinging === resolved);
    });
    updateHingingSummary();
  }

  hingingCards.forEach((card) => {
    card.addEventListener('click', () => {
      state.hinging = card.dataset.hinging;
      updateATCState();
    });
  });

  // ── Step 7: Plug & Waste ───────────────────────────────────────
  const pwCards = $qa('[data-option-group="plug-waste"] .vb-card');

  pwCards.forEach((card) => {
    card.addEventListener('click', () => {
      setActive(pwCards, card);
      state.pwFinish = card.dataset.pwFinish;
      state.pwName = card.dataset.pwName;
      state.pwHandle = card.dataset.pwHandle || '';
      state.pwVariantId = card.dataset.pwVariantId ? parseInt(card.dataset.pwVariantId, 10) : null;
      state.pwTouched = true;

      var pwImg = card.querySelector('img');
      updateSidebarThumb('pw', state.pwFinish && pwImg ? pwImg.src : '');

      updatePlugWasteDisplay();
      updatePrice();
      updateATCState();
    });
  });

  // Plug & Waste remove buttons in sidebar
  $qa('.vb-sidebar__item-remove[data-remove="plug-waste"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const noPW = $q('.vb-card--pw[data-pw-finish=""]');
      if (noPW) noPW.click();
    });
  });

  // ── Step 6: Taphole ────────────────────────────────────────────
  // Single bowl taphole cards
  const tapholeSingleCards = $qa('[data-option-group="taphole"] .vb-card');
  tapholeSingleCards.forEach((card) => {
    card.addEventListener('click', () => {
      tapholeSingleCards.forEach((c) => c.classList.remove('vb-card--active'));
      card.classList.add('vb-card--active');
      state.taphole = card.dataset.taphole;
      state.tapholeTouched = true;

      updateTapholeSidebarThumb('taphole', state.taphole);

      updateSummary('taphole', state.taphole);
      updateATCState();
    });
  });

  // Double bowl taphole click handlers are now attached dynamically
  // in renderTapholePairCards() — no static handlers needed here.

  /** Zoomed-in taphole thumbnail SVGs — same shading as main basin cards, cropped to each position */
  var TAPHOLE_THUMB_SVGS = (function () {
    // Each SVG gets its own unique gradient ID to avoid conflicts
    function basin(id) {
      return '<defs><radialGradient id="tg' + id + '" cx="50%" cy="42%" r="55%">'
        + '<stop offset="0%" stop-color="#fff"/><stop offset="50%" stop-color="#f5f5f5"/>'
        + '<stop offset="85%" stop-color="#e6e6e6"/><stop offset="100%" stop-color="#d9d9d9"/>'
        + '</radialGradient></defs>'
        + '<circle cx="50" cy="50" r="38" fill="#e0e0e0"/>'
        + '<circle cx="50" cy="50" r="36" fill="#eee"/>'
        + '<circle cx="50" cy="50" r="32" fill="url(#tg' + id + ')"/>'
        + '<circle cx="50" cy="50" r="5.5" fill="#999"/>';
    }
    function dot(cx, cy) { return '<circle cx="' + cx + '" cy="' + cy + '" r="5" fill="#c41230"/>'; }
    function svg(id, vb, extra) { return '<svg viewBox="' + vb + '">' + basin(id) + (extra || '') + '</svg>'; }
    // Rect-top (Zurich family) thumbs - rectangular top, centred bowl, tap
    // dots on the back deck. The plain '1 Taphole' / '3 Taphole' keys are
    // safe globally: Drift (the other fixed-taphole family) hides its step
    // at a single option and never sets a taphole thumb.
    function rectTop(dots) {
      var d = dots.map(function (cx) { return '<circle cx="' + cx + '" cy="22.5" r="4.5" fill="#c41230"/>'; }).join('');
      return '<svg viewBox="0 10 100 62"><rect x="0" y="15" width="100" height="56" fill="#e0e0e0"/>'
        + '<rect x="1" y="16" width="98" height="54" fill="#fbfbfb"/>'
        + '<rect x="18" y="30" width="64" height="36" rx="7" fill="#ededed" stroke="#c0c0c0" stroke-width="0.6"/>'
        + '<circle cx="50" cy="48" r="3.5" fill="#999"/>' + d + '</svg>';
    }
    return {
      'No Taphole (rect)': rectTop([]),
      '1 Taphole':         rectTop([50]),
      '3 Taphole':         rectTop([33, 50, 67]),
      'No Taphole':        svg('n', '15 2 70 45'),
      "12 O'Clock":        svg('12', '20 -6 60 42', dot(50, 2)),
      "10 O'Clock":        svg('10', '-6 2 50 44', dot(9, 18)),
      "2 O'Clock":         svg('2c', '56 2 50 44', dot(91, 18)),
      "9 O'Clock":         svg('9c', '-14 25 50 44', dot(-1, 50)),
      "3 O'Clock":         svg('3c', '64 25 50 44', dot(101, 50)),
      'Taphole in Basin':  '<svg viewBox="8 -4 84 42">'
        + '<defs><linearGradient id="tgTIBh" x1="0%" y1="50%" x2="100%" y2="50%"><stop offset="0%" stop-color="#ddd"/><stop offset="25%" stop-color="#f0f0f0"/><stop offset="50%" stop-color="#f8f8f8"/><stop offset="75%" stop-color="#f0f0f0"/><stop offset="100%" stop-color="#ddd"/></linearGradient></defs>'
        + '<rect x="8" y="6" width="84" height="76" rx="6" fill="#e0e0e0"/>'
        + '<rect x="10" y="8" width="80" height="72" rx="5" fill="#eee"/>'
        + '<rect x="14" y="24" width="72" height="52" rx="4" fill="url(#tgTIBh)"/>'
        + '<circle cx="50" cy="14" r="5" fill="#c41230"/></svg>',
    };
  })();

  function updateTapholeSidebarThumb(thumbKey, tapholeValue) {
    var svgHtml = TAPHOLE_THUMB_SVGS[tapholeValue] || TAPHOLE_THUMB_SVGS['No Taphole'];
    // Rect-top vanities swap the round-basin No Taphole thumb for the
    // matching rectangular-top drawing.
    if (hasFixedTapholeChoice && tapholeValue === 'No Taphole') {
      svgHtml = TAPHOLE_THUMB_SVGS['No Taphole (rect)'];
    }
    ['vb-thumb-' + thumbKey, 'vb-thumb-' + thumbKey + '-m'].forEach(function (id) {
      var imgEl = $id(id);
      if (!imgEl) return;
      var existing = imgEl.parentNode.querySelector('.vb-sidebar__item-thumb-svg');
      if (existing) existing.remove();
      var wrapper = document.createElement('div');
      wrapper.innerHTML = svgHtml;
      var svg = wrapper.firstChild;
      svg.classList.add('vb-sidebar__item-thumb-svg');
      svg.style.width = '48px';
      svg.style.height = '48px';
      svg.style.borderRadius = '6px';
      svg.style.flexShrink = '0';
      svg.style.background = '#f8f8f8';
      svg.style.border = '1px solid #eee';
      imgEl.parentNode.insertBefore(svg, imgEl);
      imgEl.style.display = 'none';
    });
  }

  // updateDoubleTapholeSummary removed — replaced by updateDoubleTapholePairSummary

  // ── Handle count per vanity size ──────────────────────────────
  // Maps product handle -> { size: handleCount } or { 'size|cabinetType': handleCount }
  // Defaults to 1 if not specified.
  var HANDLE_COUNTS = {
    'adp-surry-vanity': { '750': 1, '900': 1, '1200': 1, '1500': 2, '1800': 2 },
    'adp-waverley-vanity': { '750': 1, '900': 1, '1200': 2, '1500': 2, '1800': 3 },
    'adp-eli-wall-hung-vanity': { '600': 2, '750': 2, '900': 2, '1200': 4, '1500': 4, '1800': 4 },
    'adp-eli-floor-mount-vanity': { '600': 2, '750': 2, '900': 2, '1200': 4, '1500': 4, '1800': 4 },
    'adp-thea-vanity': { '750': 2, '900': 2, '1200': 2, '1500': 4, '1800': 4 },
    'adp-archie-vanity': { '900|All Door': 2, '1350|All Door': 3, '1800|All Door': 4, '1350|Door & Drawer': 5, '1800|Door & Drawer': 6 },
    'adp-london-vanity': { '600': 2, '750': 2, '900': 2, '1200': 3, '1500': 4, '1800': 4 },
  };

  function getHandleCount() {
    var productHandle = builder.dataset.productHandle;
    var counts = HANDLE_COUNTS[productHandle];
    if (!counts || !state.size) return 1;
    // Try cabinet type specific key first (e.g., "1350|All Door")
    if (state.cabinetType) {
      var cabKey = state.size + '|' + state.cabinetType;
      if (counts[cabKey]) return counts[cabKey];
    }
    // Fall back to size-only key
    if (counts[state.size]) return counts[state.size];
    return 1;
  }

  // ── Add to Cart ────────────────────────────────────────────────

  /** Session-scoped cache of storefront product JSON lookups, keyed by
   *  product handle. Avoids repeat fetches when customer cycles through
   *  selections (e.g. Gloss White → Matte White → back to Gloss White). */
  var _productFetchCache = {};

  /** Fetch /products/{handle}.js with one silent retry on failure.
   *  Shields the VB from transient issues (Cloudflare interstitials,
   *  rate limiting, network blips) that would otherwise surface as a
   *  misleading "product unavailable" message to the customer.
   *  Total max delay on transient failure: ~500ms (imperceptible).
   *  Throws only if BOTH attempts fail — real unavailability still
   *  surfaces through the .catch() in resolveHandle/resolveBasin. */
  function fetchProductJSON(handle) {
    if (_productFetchCache[handle]) {
      return Promise.resolve(_productFetchCache[handle]);
    }
    var url = '/products/' + handle + '.js';
    function attempt(retriesLeft) {
      return fetch(url, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .catch(function (err) {
          if (retriesLeft > 0) {
            return new Promise(function (resolve) {
              setTimeout(function () { resolve(attempt(retriesLeft - 1)); }, 500);
            });
          }
          throw err;
        });
    }
    return attempt(1).then(function (product) {
      _productFetchCache[handle] = product;
      return product;
    });
  }

  /** Add basin to items array, then call next.
   *  Uses productTitle for "Added with" so the cart drawer shows
   *  "Added with: ADP Waverley Vanity" (clean) instead of the URL handle.
   *  Cart Transform keys on both title and handle so this is purely a
   *  display improvement, no pricing impact. */
  function resolveBasin(items, qty, next) {
    var basinQty = qty * (state.isDouble ? 2 : 1);
    var addedWith = builder.dataset.productTitle || builder.dataset.productHandle;
    if (state.basinVariantId) {
      items.push({
        id: parseInt(state.basinVariantId, 10),
        quantity: basinQty,
        properties: { 'Added with': addedWith },
      });
      next();
    } else if (state.basinHandle && !state.basinVariantId) {
      fetchProductJSON(state.basinHandle)
        .then(function (basinProduct) {
          if (basinProduct.variants && basinProduct.variants.length > 0) {
            items.push({
              id: basinProduct.variants[0].id,
              quantity: basinQty,
              properties: { 'Added with': addedWith },
            });
          }
          next();
        })
        .catch(function () { next(); });
    } else {
      next();
    }
  }

  /** Add plug & waste product to items array, then call next.
   *  pwCount = 2 for double-bowl vanities, 1 otherwise. Cart Transform
   *  reads the cap from the vanity line (not this child line) to prevent
   *  per-line property forgery, then reprices Chrome → $0 / non-Chrome → $20. */
  function resolvePlugWaste(items, qty, next) {
    if (!state.pwVariantId) { next(); return; }
    var pwCount = (state.isDouble ? 2 : 1) * qty;
    items.push({
      id: parseInt(state.pwVariantId, 10),
      quantity: pwCount,
      properties: {
        'Added with': builder.dataset.productTitle || builder.dataset.productHandle,
      },
    });
    next();
  }

  /** Add handle product to items array, then call next.
   *  Uses fetchProductJSON which silently retries once on transient
   *  failures, so customers don't see "unavailable" on a network blip
   *  or CF challenge. Error only surfaces if BOTH attempts genuinely
   *  fail — real handle unavailability still triggers the customer
   *  alert via handleATC's abortATC path. */
  function resolveHandle(items, qty, next) {
    if (!state.handleProductHandle || state.hardwareType === 'push-to-open' || state.hardwareType === 'finger-pull') {
      next();
      return;
    }
    var handleCount = getHandleCount() * qty;
    fetchProductJSON(state.handleProductHandle)
      .then(function (handleProduct) {
        if (handleProduct.variants && handleProduct.variants.length > 0) {
          items.push({
            id: handleProduct.variants[0].id,
            quantity: handleCount,
            properties: {
              'Added with': builder.dataset.productTitle || builder.dataset.productHandle,
            },
          });
          next();
        } else {
          next(new Error('Handle product has no variants'));
        }
      })
      .catch(function () {
        next(new Error('The selected handle is currently unavailable. Please pick a different finish.'));
      });
  }

  /** Express-equivalence upgrade (Chris 2026-08-03): a Standard build that
   *  lands EXACTLY on the Express stocked formula (express-eligible
   *  size/bowl, express finish, 1 taphole, right-hand drawers / fixed mini
   *  orientation) IS the express product, so the cart should say so - two
   *  identical builds must not read differently just because of the path
   *  taken. The builder UI deliberately stays on the customer's Standard
   *  selection (no mid-flow surprises); only the committed variant swaps,
   *  which makes the cart line read "... / Express Ship" with the express
   *  SKU and express barcode, exactly like a real express selection.
   *  Guards: never swap across a price difference (pricing is managed in
   *  admin and may diverge), never swap onto a sold-out express variant
   *  (standard made-to-order always stands), and only at quantity 1 (stock
   *  depth is invisible client-side - a multi-unit swap could fail at
   *  /cart/add where the standard build would have succeeded).
   *  On non-express vanities no "|Express Ship" lookup key exists, so this
   *  returns null everywhere else. */
  function expressEquivalentVariant(qty) {
    var exFinishes = VB_CONFIG.expressFinishes || [];
    if (!exFinishes.length || !VB_CONFIG.variantLookup) return null;
    if (state.dispatch === 'Express Ship') return null;
    if (qty !== 1) return null;
    if (!state.size || !state.finish) return null;
    if (!sizesOnly && !state.bowl) return null;
    if (exFinishes.indexOf(state.finish) === -1) return null;
    if (hasFixedTapholeChoice && state.taphole !== '1 Taphole') return null;
    if (hasHingeStep && state.hinge !== 'Left') return null;
    if (hasHingingFeature && resolveHinging() !== 'Standard') return null;
    var sizeBowl = sizesOnly ? state.size + 'mm' : state.size + 'mm ' + state.bowl;
    if (state.cabinetType) return null;
    // Choice sizes must have been picked right-hand; auto-derived sizes
    // (offsets/doubles/600s) resolve identically on both paths already.
    if (hasDrawerFeature && drawerSideValues.indexOf(sizeBowl) !== -1
        && state.drawer !== 'Right-hand') return null;
    var v = VB_CONFIG.variantLookup[sizeBowl + '|Express Ship'];
    if (!v) return null;
    if (v.available === false) return null;
    if (v.price !== state.variantPrice) return null;
    return v;
  }

  function handleATC(e) {
    const btn = e.currentTarget;
    if (btn.disabled) return;

    btn.classList.add('loading');
    btn.disabled = true;

    // Build cart items array
    const items = [];

    // Item 1: Vanity (with handle noted as a text property for reference)
    const vanityProps = {
      'Cabinet Finish': state.finish,
    };
    // Only emit Handle prop when a handle is actually part of the config —
    // Cabinet Only skips hardware entirely, so omit the prop rather than
    // emit a null value.
    if (state.hardware) {
      vanityProps['Handle'] = state.handleFinish ? state.hardware + ' - ' + state.handleFinish : state.hardware;
    }
    if (state.topType) vanityProps['Top'] = state.topType;
    if (state.thickness) {
      vanityProps['Benchtop Thickness'] = state.thickness;
      if (state.thicknessSurcharge > 0) {
        vanityProps['_Surcharge'] = String(state.thicknessSurcharge / 100);
      }
    }
    if (!state.skipBenchtop && state.benchtopMaterial && state.benchtopName) {
      vanityProps['Benchtop'] = state.benchtopMaterial + ' - ' + state.benchtopName;
    }
    // Per-unit caps for Cart Transform cumulative discount enforcement.
    // Cart Transform multiplies by line.quantity to get the total cap for
    // all child lines (handles, P&W) referencing this vanity via "Added with".
    if (state.handleProductHandle && state.hardwareType !== 'push-to-open' && state.hardwareType !== 'finger-pull') {
      vanityProps['_Max handles'] = String(getHandleCount());
    }
    if (state.pwVariantId) {
      vanityProps['_Max pw_count'] = String(state.isDouble ? 2 : 1);
    }
    // Per-vanity premium-handle override flag for Cart Transform.
    // Set on Tier A/C vanities (Surry, Thea, London, Waverley, Archie). Cart
    // Transform reads "_Premium handles included" off the vanity line and
    // prices premium handles in the same caps pool at $0 instead of $20.
    //
    // Glacier (premiumHandlePricing === 'variant') sets the flag based on
    // the customer's selected Tier (Pro = included, Lite = not included).
    if (VB_CONFIG.premiumHandlePricing === 'included') {
      vanityProps['_Premium handles included'] = '1';
    } else if (VB_CONFIG.premiumHandlePricing === 'variant') {
      var glacierTier = (window.VBGlacier && window.VBGlacier.state) ? window.VBGlacier.state.tier : null;
      if (glacierTier === 'Pro') {
        vanityProps['_Premium handles included'] = '1';
      }
    }
    // Glacier-specific axes that other vanities don't have — adds them
    // to the cart line as customer-facing properties so they appear in
    // order summary, invoices, etc.
    if (window.VBGlacier && window.VBGlacier.state) {
      var g = window.VBGlacier.state;
      if (g.tier) vanityProps['Tier'] = g.tier;
      if (g.doorConfig) vanityProps['Door Configuration'] = g.doorConfig;
      if (g.style) vanityProps['Style'] = g.style;
    }
    // Fixed-taphole vanities (Zurich family) drill the whole top to one
    // spec, so doubles carry the single Taphole property too - the
    // per-basin pair only applies to basin-driven vanities.
    if (state.isDouble && !hasFixedTaphole) {
      if (state.tapholeLeft) vanityProps['Taphole (Left)'] = state.tapholeLeft;
      if (state.tapholeRight) vanityProps['Taphole (Right)'] = state.tapholeRight;
    } else if (state.taphole) {
      vanityProps['Taphole'] = state.taphole;
    }
    if (state.hinge) {
      vanityProps['Door Hinge'] = state.hinge;
    }
    // Drawer-configured vanities always state their drawer situation -
    // chosen on choice sizes, derived everywhere else - so the order
    // screen never needs inference from bowl position.
    if (hasDrawerFeature) {
      var drawerSide = resolveDrawerSide();
      if (drawerSide) vanityProps['Drawer Side'] = drawerSide;
    }
    // Hinging-configured products (Zurich Shaving Cabinet) always state
    // their hinging - picked on 1500/1800, "Standard" everywhere else.
    if (hasHingingFeature) {
      var hinging = resolveHinging();
      if (hinging) vanityProps['Hinging'] = hinging;
    }

    const qtyInput = document.getElementById('vb-qty-input');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    // Standard build matching the Express formula exactly - commit the
    // Express variant so the cart line flags it honestly (see
    // expressEquivalentVariant above for the guards).
    const expressUpgrade = expressEquivalentVariant(qty);

    items.push({
      id: parseInt(expressUpgrade ? expressUpgrade.id : state.variantId, 10),
      quantity: qty,
      properties: vanityProps,
    });

    // Item 2: Basin (async) → Item 3: Handle (async) → Item 4: P&W → submit
    // Any resolver may pass an Error to abort the chain with a user alert.
    function abortATC(err) {
      btn.classList.remove('loading');
      btn.disabled = false;
      alert(err && err.message ? err.message : 'There was an error adding to cart. Please try again.');
    }
    resolveBasin(items, qty, function (err) {
      if (err) return abortATC(err);
      resolveHandle(items, qty, function (err) {
        if (err) return abortATC(err);
        resolvePlugWaste(items, qty, function (err) {
          if (err) return abortATC(err);
          submitCart(items, btn);
        });
      });
    });
  }

  function submitCart(items, btn) {
    // Project Studio hook: when the builder runs inside the planner, hand the
    // fully-assembled bundle to the planner instead of posting to cart, so the
    // configured vanity becomes a project item (added to cart later with the
    // whole room). The PDP never sets this global, so PDP behaviour is unchanged.
    if (typeof window.VB_PROJECT_ADD === 'function') {
      window.VB_PROJECT_ADD(items);
      if (btn) { btn.classList.remove('loading'); btn.disabled = false; }
      return;
    }
    var cartDrawer = document.querySelector('cart-drawer');
    var sectionsParam = {};
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      var sectionIds = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
      sectionsParam.sections = sectionIds.join(',');
    }

    var bodyData = { items: items };
    if (sectionsParam.sections) bodyData.sections = sectionsParam.sections;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bodyData),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart error');
        return r.json();
      })
      .then(function (response) {
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(response);
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
        } else if (typeof window.openAtelierCart === 'function') {
          // Atelier theme has no Dawn <cart-drawer> — open its own drawer
          // (which re-fetches the cart) instead of redirecting to /cart.
          window.openAtelierCart();
        } else {
          window.location.href = '/cart';
        }
        btn.classList.remove('loading');
        btn.disabled = false;
      })
      .catch(function (err) {
        console.error('Vanity Builder ATC error:', err);
        btn.classList.remove('loading');
        btn.disabled = false;
        alert('There was an error adding to cart. Please try again.');
      });
  }

  // Bind ATC buttons
  ['vb-atc-btn', 'vb-atc-btn-m'].forEach((id) => {
    const btn = $id(id);
    if (btn) btn.addEventListener('click', handleATC);
  });

  // ── Required-field nudge on disabled ATC click ────────────────
  // Map sidebar summary keys to their scroll targets in the builder
  var summaryToStep = {
    size: { step: '1', target: '.vb-cards[data-option-group="size"]' },
    bowl: { step: '1', target: '.vb-bowl-cards' },
    finish: { step: '2', target: '[data-option-group="finish"]' },
    benchtop: { step: '3', target: '[data-option-group="benchtop"]' },
    hardware: { step: '4', target: '[data-option-group="hardware"]' },
  };

  function pulseElement(el, className) {
    el.classList.remove(className);
    void el.offsetWidth; // reflow to restart animation
    el.classList.add(className);
    el.addEventListener('animationend', function handler() {
      el.classList.remove(className);
      el.removeEventListener('animationend', handler);
    });
  }

  function scrollThenPulse(scrollTarget, pulseTarget, pulseClass) {
    // Put the top of the target at the vertical middle of the screen
    var header = document.querySelector('.section-header, .shopify-section-header, header');
    var headerH = header ? header.offsetHeight : 80;
    var elTop = scrollTarget.getBoundingClientRect().top + window.pageYOffset;
    var y = elTop - headerH - (window.innerHeight - headerH) * 0.25;
    window.scrollTo({ top: y, behavior: 'smooth' });

    // Wait for scroll to finish before pulsing — use IntersectionObserver
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          observer.disconnect();
          // Small delay so the eye settles after scroll stops
          setTimeout(function () {
            pulseElement(pulseTarget, pulseClass);
          }, 150);
        }
      });
    }, { threshold: 0.3 });
    observer.observe(scrollTarget);
  }

  function getUnfilledRequired() {
    return Array.from($qa('.vb-sidebar__item[data-required="true"]')).filter(function (item) {
      return !item.classList.contains('vb-sidebar__item--filled');
    });
  }

  // Invisible overlay button over disabled ATC — intercepts clicks
  function createATCOverlay(atcBtn) {
    var overlay = document.createElement('button');
    overlay.type = 'button';
    overlay.className = 'vb-atc-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.tabIndex = -1;
    // Wrap the ATC button so overlay is scoped to it only
    var wrap = document.createElement('div');
    wrap.className = 'vb-atc-wrap';
    atcBtn.parentNode.insertBefore(wrap, atcBtn);
    wrap.appendChild(atcBtn);
    wrap.appendChild(overlay);
    return overlay;
  }

  var atcOverlays = [];
  ['vb-atc-btn', 'vb-atc-btn-m'].forEach(function (id) {
    var btn = $id(id);
    if (!btn) return;
    var overlay = createATCOverlay(btn);
    atcOverlays.push(overlay);

    overlay.addEventListener('click', function (e) {
      e.preventDefault();
      var unfilled = getUnfilledRequired();
      unfilled.forEach(function (item) {
        pulseElement(item, 'vb-sidebar__item--pulse');
      });
    });
  });

  // Show/hide overlays based on ATC enabled state
  var origUpdateATCState = updateATCState;
  updateATCState = function () {
    origUpdateATCState();
    var isDisabled = true;
    var btn = $id('vb-atc-btn');
    if (btn) isDisabled = btn.disabled;
    atcOverlays.forEach(function (ov) {
      ov.classList.toggle('d-none', !isDisabled);
    });
  };

  // Click unfilled required sidebar row → scroll to step and pulse it
  $qa('.vb-sidebar__item[data-required="true"]').forEach(function (item) {
    item.addEventListener('click', function () {
      if (item.classList.contains('vb-sidebar__item--filled')) return;
      var key = item.dataset.summary;
      var mapping = summaryToStep[key];
      if (!mapping) return;

      // Find the scroll target
      var target = $q(mapping.target);
      if (!target) target = $q('.vb-step[data-step="' + mapping.step + '"]');
      if (!target) return;

      // Find the step to pulse
      var step = target.closest('.vb-step') || $q('.vb-step[data-step="' + mapping.step + '"]');
      if (!step) return;

      scrollThenPulse(target, step, 'vb-step--pulse');
    });
  });

  // ── Quantity +/- buttons ──────────────────────────────────────
  $qa('.vb-sidebar__qty-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const wrap = btn.closest('.vb-sidebar__qty');
      const input = wrap.querySelector('.vb-sidebar__qty-input');
      let val = parseInt(input.value, 10) || 1;

      if (btn.dataset.qtyAction === 'minus') {
        val = Math.max(1, val - 1);
      } else {
        val = Math.min(99, val + 1);
      }
      input.value = val;

      // Sync desktop and mobile inputs
      $qa('.vb-sidebar__qty-input').forEach((inp) => { inp.value = val; });
    });
  });

  // ── Init mobile sticky total bar ─────────────────────────────
  initStickyBar();

  // ─────────────────────────────────────────────────────────────
  // Glacier integration: expose internal state + updatePrice so the
  // parallel vanity-builder-glacier.js can feed the resolved variant's
  // price (which comes from the section endpoint, not local variantLookup)
  // through the EXISTING price pipeline. This gives Glacier the same
  // animated price changes, +/-$X delta indicator, savings calculation,
  // surcharge integration, etc. that other vanities get for free.
  //
  // Other vanities never read window.VBShared so they're unaffected.
  // ─────────────────────────────────────────────────────────────
  window.VBShared = { state: state, updatePrice: updatePrice };
})();
